import { useEffect, useRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search, Share2, Image, Video, Youtube, Music, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
  Crosshair, Highlighter, MousePointer, MessageCircle, Check,
  Mic, Camera, Timer, Square, BookOpen, Pencil, Eraser, Trash2, StickyNote, X, AlertTriangle, Languages
} from 'lucide-react';
import AlertModal from './AlertModal';
import StarsResultModal from './StarsResultModal';
import RotateDeviceOverlay from './RotateDeviceOverlay';
import { InputFieldElement, SpinWheelElement, FlipCardElement, DiceElement, ExerciseElement, AnagramElement, BoggleElement, WordPoolElement, OddOneOutElement, SortingElement, UnjumbleElement, OrderSentencesElement, validateOrderSentences, getOrderSentencesFromContent, RankElement, validateRank, CheckboxElement, LikeElement, RatingElement, DragLabelImageElement, DragLabelTextElement, DragLabelBlankElement, TableElement, MatchLinesElement, LabelImageElement, TypeAnswerElement, TrueFalseElement, AbcdElement, gradeAbcd, ChooseWordElement, FixMistakeElement, FillGapsElement, computeFillGapsResults, TableFillElement, computeTableFillResults, sanitizeTableFillContent, PronounceElement, ExerciseSoundBadge, supportsSoundBadge, SoundElement, ArrowElement, PuzzleElement, FindObjectElement, FindStickerElement, FrameElement, MissingLettersElement, AliasElement, WhoAmIElement, NmtImageElement, NotebookElement, ShopElement, CardDeckElement, BookElement, HighlightElement, SmittnikElement, BingoElement, MemoryElement, PersonalityQuizElement, KissMarryAvoidElement, BoardGameElement, SpotDifferenceElement, MindMapElement, DialogueElement, PictionaryElement, TabooElement, DressUpElement, GifImageLayer, gradeMissingLetters, normalizeTrueFalseLetter, gradeChooseWord, parseChooseWordSentence, gradeFixMistake, parseFixMistakeSentence, validateUnjumble, getUnjumbleSentences } from './presentation-editor/elements';
import { computePronounceScore, scoreToStars } from './presentation-editor/elements/PronounceElement';
import { getHighlightBoxShadow, isHighlightClickable } from './presentation-editor/highlightUtils';
import { getImageFilterCss } from './presentation-editor/imageFilterUtils';
import { TimerModal, RatingModal, TeacherReportModal, ClassroomChat, DrawingCanvas, SyncedVideo, SyncedYouTubeVideo, SyncedAudio, LiveVocabularyModal } from './classroom';
import { useAuth } from '../contexts/AuthContext';
import useEducationStore from '../stores/educationStore';
import useClassroomStore from '../stores/classroomStore';
import useChatStore from '../stores/chatStore';
import useVideoStore from '../stores/videoStore';
import { useVirtualClassroom } from '../hooks/useVirtualClassroom';
import { API } from '../config/api';
import { api } from '../utils/api';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { renderTeacherNotes } from '../utils/renderTeacherNotes';
import { VideoRoom, SmartVideoGallery, VideoControls } from './video';
import TeacherNotesBroadcaster from './video/TeacherNotesBroadcaster';
import SlideCapturePublisher from './video/SlideCapturePublisher';
import LessonRecorder from './video/LessonRecorder';
import { useTranslation } from '../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const VirtualClassroom = () => {
  const navigate = useNavigate();
  const { userRole, user } = useAuth();
  const { t } = useTranslation();
  const tt = (k, v) => t(`virtualClassroom.${k}`, v);
  const currentLessonForClass = useClassroomStore(s => s.currentLessonForClass);
  const onEndClass = useClassroomStore(s => s.endClass);
  const participants = useClassroomStore(s => s.participants);
  const startPresencePolling = useClassroomStore(s => s.startPresencePolling);
  const stopPresencePolling = useClassroomStore(s => s.stopPresencePolling);

  // Timer state from store
  const timerActive = useClassroomStore(s => s.timerActive);
  const timerRemainingSeconds = useClassroomStore(s => s.timerRemainingSeconds);
  const timerJustEnded = useClassroomStore(s => s.timerJustEnded);
  const startTimer = useClassroomStore(s => s.startTimer);
  const stopTimer = useClassroomStore(s => s.stopTimer);
  const updateTimerLocally = useClassroomStore(s => s.updateTimerLocally);
  const clearTimerJustEnded = useClassroomStore(s => s.clearTimerJustEnded);
  const startTimerPolling = useClassroomStore(s => s.startTimerPolling);
  const stopTimerPolling = useClassroomStore(s => s.stopTimerPolling);

  // Auto-end (warning at scheduled_end + 5min, hard end at +10min)
  const autoEndWarningActive = useClassroomStore(s => s.autoEndWarningActive);
  const autoEndRemainingSec = useClassroomStore(s => s.autoEndRemainingSec);
  const updateAutoEndCountdown = useClassroomStore(s => s.updateAutoEndCountdown);

  // Presentation state from store (synced)
  const presentationSharedFromStore = useClassroomStore(s => s.presentationShared);
  const syncedSlideIndex = useClassroomStore(s => s.syncedSlideIndex);
  const syncedPresentationId = useClassroomStore(s => s.syncedPresentationId);
  const updatePresentationState = useClassroomStore(s => s.updatePresentationState);
  const startPresentationPolling = useClassroomStore(s => s.startPresentationPolling);
  const stopPresentationPolling = useClassroomStore(s => s.stopPresentationPolling);

  // Video playback sync (teacher controls, students mirror)
  const startVideoStatePolling = useClassroomStore(s => s.startVideoStatePolling);
  const stopVideoStatePolling = useClassroomStore(s => s.stopVideoStatePolling);

  // Student answers sync
  const studentAnswersMap = useClassroomStore(s => s.studentAnswersMap);
  const sendStudentAnswers = useClassroomStore(s => s.sendStudentAnswers);
  const startAnswersPolling = useClassroomStore(s => s.startAnswersPolling);
  const stopAnswersPolling = useClassroomStore(s => s.stopAnswersPolling);

  // Student exercise answers sync (teacher sees student quiz selections + results)
  const studentExerciseAnswersMap = useClassroomStore(s => s.studentExerciseAnswersMap);
  const startExerciseAnswersPolling = useClassroomStore(s => s.startExerciseAnswersPolling);
  const stopExerciseAnswersPolling = useClassroomStore(s => s.stopExerciseAnswersPolling);

  // Teacher answers sync (for teacher editing editable elements)
  const teacherAnswersMap = useClassroomStore(s => s.teacherAnswersMap);
  const sendTeacherAnswers = useClassroomStore(s => s.sendTeacherAnswers);
  const startTeacherAnswersPolling = useClassroomStore(s => s.startTeacherAnswersPolling);
  const stopTeacherAnswersPolling = useClassroomStore(s => s.stopTeacherAnswersPolling);

  // Element positions sync
  const syncedElementPositions = useClassroomStore(s => s.syncedElementPositions);
  const sendElementPositions = useClassroomStore(s => s.sendElementPositions);
  const startPositionsPolling = useClassroomStore(s => s.startPositionsPolling);
  const stopPositionsPolling = useClassroomStore(s => s.stopPositionsPolling);
  const trackLocalDrag = useClassroomStore(s => s.trackLocalDrag);
  const clearLocalDragTimestamp = useClassroomStore(s => s.clearLocalDragTimestamp);
  const localDragTimestamps = useClassroomStore(s => s.localDragTimestamps);

  // Interactive elements state sync (dice, wheel, flip cards, input fields)
  const syncedInteractiveStates = useClassroomStore(s => s.syncedInteractiveStates);
  const sendInteractiveState = useClassroomStore(s => s.sendInteractiveState);
  const startInteractivePolling = useClassroomStore(s => s.startInteractivePolling);
  const stopInteractivePolling = useClassroomStore(s => s.stopInteractivePolling);
  const trackLocalInteraction = useClassroomStore(s => s.trackLocalInteraction);
  const clearLocalInteractionTimestamp = useClassroomStore(s => s.clearLocalInteractionTimestamp);
  const setLocalInteractiveState = useClassroomStore(s => s.setLocalInteractiveState);

  // Drawing state sync (pencil and eraser for both teacher and student)
  const drawingStrokes = useClassroomStore(s => s.drawingStrokes);
  const drawingTool = useClassroomStore(s => s.drawingTool);
  const drawingColor = useClassroomStore(s => s.drawingColor);
  const drawingSize = useClassroomStore(s => s.drawingSize);
  const setDrawingTool = useClassroomStore(s => s.setDrawingTool);
  const setDrawingColor = useClassroomStore(s => s.setDrawingColor);
  const setDrawingSize = useClassroomStore(s => s.setDrawingSize);
  const addLocalStroke = useClassroomStore(s => s.addLocalStroke);
  const sendDrawingStroke = useClassroomStore(s => s.sendDrawingStroke);
  const eraseStrokes = useClassroomStore(s => s.eraseStrokes);
  const clearAllStrokes = useClassroomStore(s => s.clearAllStrokes);
  const startDrawingPolling = useClassroomStore(s => s.startDrawingPolling);
  const stopDrawingPolling = useClassroomStore(s => s.stopDrawingPolling);

  // Teacher pointer sync (teacher broadcasts cursor position; students mirror)
  const syncedPointerState = useClassroomStore(s => s.syncedPointerState);
  const sendPointerState = useClassroomStore(s => s.sendPointerState);
  const startPointerStatePolling = useClassroomStore(s => s.startPointerStatePolling);
  const stopPointerStatePolling = useClassroomStore(s => s.stopPointerStatePolling);

  // Video store state
  const videoToken = useVideoStore(s => s.token);
  const livekitUrl = useVideoStore(s => s.livekitUrl);
  // Camera/mic toggle values captured in CameraPreviewModal; passed to VideoRoom
  // so devices left OFF in the preview stay OFF on connect.
  const initialCameraEnabled = useClassroomStore(s => s.initialCameraEnabled);
  const initialMicEnabled = useClassroomStore(s => s.initialMicEnabled);
  const fetchVideoToken = useVideoStore(s => s.fetchToken);
  const disconnectVideo = useVideoStore(s => s.disconnect);
  const resetVideo = useVideoStore(s => s.reset);

  const presentations = useEducationStore(s => s.presentations);
  const fetchPresentationWithSlides = useEducationStore(s => s.fetchPresentationWithSlides);
  const programs = useEducationStore(s => s.programs);
  const studentPrograms = useEducationStore(s => s.studentPrograms);
  const onSetStudentPrograms = useEducationStore(s => s.setStudentPrograms);
  
  // Chat store - individual chat
  const messages = useChatStore(s => s.messages);
  const sendMessage = useChatStore(s => s.sendMessage);
  const fetchMessages = useChatStore(s => s.fetchMessages);
  const startPolling = useChatStore(s => s.startPolling);
  const stopPolling = useChatStore(s => s.stopPolling);
  const startConversation = useChatStore(s => s.startConversation);

  // Chat store - club group chat
  const clubMessages = useChatStore(s => s.clubMessages);
  const sendClubMessage = useChatStore(s => s.sendClubMessage);
  const fetchClubMessages = useChatStore(s => s.fetchClubMessages);
  const startClubPolling = useChatStore(s => s.startClubPolling);
  const stopClubPolling = useChatStore(s => s.stopClubPolling);

  // Chat state
  const [conversationId, setConversationId] = useState(null);
  const [chatAvailable, setChatAvailable] = useState(true);

  // Mobile single-view participant gallery state
  const [mobileVideoIndex, setMobileVideoIndex] = useState(0);
  const [mobileVideoCount, setMobileVideoCount] = useState(0);
  // Mobile: allow the student to collapse the floating video PiP so the slide
  // takes the whole viewport when they want to focus on the presentation.
  const [mobileVideoCollapsed, setMobileVideoCollapsed] = useState(false);
  useEffect(() => {
    if (mobileVideoCount > 0 && mobileVideoIndex >= mobileVideoCount) {
      setMobileVideoIndex(0);
    }
  }, [mobileVideoCount, mobileVideoIndex]);

  // Disable iOS Safari pull-to-refresh while in the lesson: a downward drag
  // on the phone was reloading the page and kicking students out mid-lesson.
  // Scoped to VirtualClassroom via a class on <html> that index.css targets.
  useEffect(() => {
    document.documentElement.classList.add('no-pull-refresh');
    return () => {
      document.documentElement.classList.remove('no-pull-refresh');
    };
  }, []);

  // Determine if this is a club lesson for group chat
  const isClubLesson = useMemo(() => {
    const sourceType = currentLessonForClass?.sourceType || currentLessonForClass?.source_type;
    return sourceType === 'club' || sourceType === 'club_session';
  }, [currentLessonForClass?.sourceType, currentLessonForClass?.source_type]);

  const clubId = useMemo(() => {
    if (!isClubLesson) return null;
    return currentLessonForClass?.clubId || currentLessonForClass?.club_id || currentLessonForClass?.sourceClubId;
  }, [isClubLesson, currentLessonForClass?.clubId, currentLessonForClass?.club_id, currentLessonForClass?.sourceClubId]);

  // Timer state
  const [showTimerModal, setShowTimerModal] = useState(false);
  const timerIntervalRef = useRef(null);

  // Live vocabulary modal (teacher-only): mid-lesson word push into student's homework
  const [showLiveVocabModal, setShowLiveVocabModal] = useState(false);

  // Chat scroll ref
  const chatEndRef = useRef(null);

  // Drag optimization refs
  const [draggingElementId, setDraggingElementId] = useState(null);
  const dragRafRef = useRef(null);
  const cachedRectRef = useRef(null);
  const pendingPositionRef = useRef(null);
  const clearDragTimeoutRef = useRef(null);
  // Elements dropped into a smittnik, keyed per slide. Local-only for now —
  // trashing is a session UX, not persisted; resets on reload or slide reset.
  const [trashedElements, setTrashedElements] = useState({});
  // Drag start snapshot: cursor position + every group member's starting position
  // (as % of board). Drag-move applies the cursor delta to each member so grouped
  // elements move as one unit instead of only the element under the finger.
  const dragStartRef = useRef(null);

  // Throttle teacher pointer broadcasts so we don't spam the server on every mouse move
  const pointerBroadcastRef = useRef({ lastSentAt: 0, scheduled: null, lastPayload: null });

    // Alert modal state
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const showAlert = (title, message, type = 'info') => {
      setAlertModal({ isOpen: true, title, message, type });
    };

    const closeAlert = () => {
      setAlertModal({ isOpen: false, title: '', message: '', type: 'info' });
    };

    // Right sidebar tab: 'chat' for everyone; teachers can switch to 'notes' to see
    // the slide's teacher-only notes in the same panel instead of on the toolbar.
    const [sidebarTab, setSidebarTab] = useState('chat');

    // Chat visibility toggle. On lg+ chat is always visible inline. On <lg (landscape
    // phones + small tablets) chat is hidden by default and the student opens it via the
    // toolbar chat button. When open, it sits as an inline sibling column, not an overlay.
    const [chatOpen, setChatOpen] = useState(false);

    // Interactive elements local state (for immediate feedback)
    const [localInteractiveUI, setLocalInteractiveUI] = useState({
      wheelRotations: {},   // { elementId: rotation }
      flipStates: {},       // { elementId: isFlipped }
      diceValues: {},       // { elementId: value }
      inputValues: {},      // { elementId: value }
      checkboxStates: {},   // { elementId: checked }
      likeStates: {},       // { elementId: liked }
      ratingStates: {},     // { elementId: rating (0..N) }
      shopStates: {},       // { elementId: purchasedIds[] }
      cardDeckStates: {},   // { elementId: movedIds[] }
      bookStates: {},       // { elementId: flippedCount }
      highlightStates: {},  // { elementId: revealed }
      bingoStates: {},      // { elementId: crossedIds[] }
      kissMarryAvoidStates: {} // { elementId: { characterId: 'kiss'|'marry'|'avoid' } }
    });

    // Exercise local state (for tracking answers and results)
    const [exerciseLocalState, setExerciseLocalState] = useState({});
    const [anagramLocalState, setAnagramLocalState] = useState({});
    const [boggleLocalState, setBoggleLocalState] = useState({});
    const [dragLabelLocalState, setDragLabelLocalState] = useState({});
    const [matchLinesLocalState, setMatchLinesLocalState] = useState({});
    const [labelImageLocalState, setLabelImageLocalState] = useState({});
    const [typeAnswerLocalState, setTypeAnswerLocalState] = useState({});
    const [missingLettersLocalState, setMissingLettersLocalState] = useState({});
    const [trueFalseLocalState, setTrueFalseLocalState] = useState({});
    const [abcdLocalState, setAbcdLocalState] = useState({});
    const [chooseWordLocalState, setChooseWordLocalState] = useState({});
    const [fixMistakeLocalState, setFixMistakeLocalState] = useState({});
    const [fillGapsLocalState, setFillGapsLocalState] = useState({});
    const [tableFillLocalState, setTableFillLocalState] = useState({});
    const [oddOneOutLocalState, setOddOneOutLocalState] = useState({});
    const [sortingLocalState, setSortingLocalState] = useState({});
    const [unjumbleLocalState, setUnjumbleLocalState] = useState({});
    const [orderSentencesLocalState, setOrderSentencesLocalState] = useState({});
    const [rankLocalState, setRankLocalState] = useState({});
    const [pronounceLocalState, setPronounceLocalState] = useState({});
    const [findStickerLocalState, setFindStickerLocalState] = useState({});
    // Stars result for the student. Populated by polling from the computed
    // score so the student sees their live progress; the modal only opens
    // when the student clicks "Завершити урок" (see handleEndLesson).
    const [starsResult, setStarsResult] = useState(null);
    const [showStarsResult, setShowStarsResult] = useState(false);
    // True while handleEndLesson is claiming the student's stars so we can
    // show a brief overlay instead of blinking straight into the modal.
    const [endingLesson, setEndingLesson] = useState(false);
    // Set when fetching the lesson's presentation returns null (403/404/network).
    // Drives the classroom's error UI so the teacher isn't stuck on an
    // infinite spinner when the deck can't load.
    const [presentationLoadError, setPresentationLoadError] = useState(false);
    // Track whether the last magnifier update came from a touch event so the lens
    // can be offset above the finger — otherwise the finger covers the zoomed area.
    const magnifierTouchModeRef = useRef(false);
    const [magnifierTouchMode, setMagnifierTouchMode] = useState(false);
    // { elementId: { selectedAnswers: [], showResult: bool, isCorrect: bool } }

    // Homework is assigned automatically by the backend scheduler 60 min after the
    // scheduled lesson start (see backend/utils/scheduler.js → autoAssignHomeworkAfterLesson).
    // Clubs do not have homework.

    const {
      showRatingModal, setShowRatingModal,
      showTeacherReport, setShowTeacherReport,
      classSlideIndex, setClassSlideIndex,
      studentElementPositions, setStudentElementPositions,
      studentInputValues, setStudentInputValues,
      laserActive, setLaserActive,
      highlighterActive, setHighlighterActive,
      magnifierActive, setMagnifierActive,
      magnifierPosition, setMagnifierPosition,
      pointerActive, setPointerActive,
      pointerPosition, setPointerPosition,
      presentationShared, setPresentationShared,
      refs: { boardRef },
    } = useVirtualClassroom();

    // Poll for the student's stars result. Fires only for the student role
    // and stops once the balance has been credited (awarded=true). Before
    // that we still surface the computed value so the modal can open
    // immediately when the student clicks "Завершити урок" — no wait on the
    // teacher.
    useEffect(() => {
      if (userRole !== 'student') return;
      const lessonId = currentLessonForClass?.id;
      if (!lessonId || String(lessonId).startsWith('club')) return;

      let cancelled = false;
      let awardedSeen = false;
      const check = async () => {
        try {
          const res = await fetch(`/api/schedule/lesson/${lessonId}/my-stars`, {
            credentials: 'include',
          });
          if (!res.ok || cancelled) return;
          const data = await res.json();
          setStarsResult({
            stars: data.stars ?? 0,
            percentage: data.percentage ?? 0,
            earnedPoints: data.earnedPoints ?? 0,
            totalPoints: data.totalPoints ?? 0,
          });
          if (data?.awarded) awardedSeen = true;
        } catch (err) {
          // Silent — will retry on next tick.
        }
      };
      check();
      const interval = setInterval(() => {
        if (awardedSeen) return;
        check();
      }, 15000);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [userRole, currentLessonForClass?.id]);

    // Board scale for consistent rendering with editor (960x540 reference)
    const REFERENCE_WIDTH = 960;
    const REFERENCE_HEIGHT = 540;
    const REFERENCE_RATIO = REFERENCE_WIDTH / REFERENCE_HEIGHT;
    const [boardScale, setBoardScale] = useState(1);
    const [boardSize, setBoardSize] = useState({ width: 0, height: 0 });

    // rAF-throttled magnifier position updates (smooth dragging on mobile)
    const magnifierRafRef = useRef(null);
    const magnifierPendingPosRef = useRef(null);
    // Live-DOM cloning refs for the magnifier: source is the slide-contents
    // wrapper (all rendered elements), target is the lens interior. Cloning
    // covers every interactive element type without duplicating render logic.
    const slideContentsRef = useRef(null);
    const magnifierLensInnerRef = useRef(null);
    const scheduleMagnifierUpdate = useCallback((pos) => {
      magnifierPendingPosRef.current = pos;
      if (magnifierRafRef.current) return;
      magnifierRafRef.current = requestAnimationFrame(() => {
        magnifierRafRef.current = null;
        if (magnifierPendingPosRef.current) {
          setMagnifierPosition(magnifierPendingPosRef.current);
          magnifierPendingPosRef.current = null;
        }
      });
    }, [setMagnifierPosition]);
    useEffect(() => () => {
      if (magnifierRafRef.current) cancelAnimationFrame(magnifierRafRef.current);
    }, []);

    // Keep the magnifier lens in sync with the live slide DOM. Runs after every
    // render so interactive state (flip cards, dice, exercise selections, input
    // values, etc.) stays current inside the lens without duplicating each
    // element type's render logic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
      const lens = magnifierLensInnerRef.current;
      const source = slideContentsRef.current;
      if (!magnifierActive || !lens || !source) return;
      const clone = source.cloneNode(true);
      // Copy form-control values — cloneNode doesn't preserve them.
      const origFields = source.querySelectorAll('input, textarea, select');
      const cloneFields = clone.querySelectorAll('input, textarea, select');
      origFields.forEach((orig, i) => {
        const c = cloneFields[i];
        if (!c) return;
        if (c.type === 'checkbox' || c.type === 'radio') {
          c.checked = orig.checked;
          if (orig.checked) c.setAttribute('checked', ''); else c.removeAttribute('checked');
        } else {
          c.value = orig.value;
          c.setAttribute('value', orig.value);
        }
      });
      clone.style.pointerEvents = 'none';
      lens.replaceChildren(clone);
    });

    // Compute fit-contain dimensions inside the board's parent container so the
    // 16:9 slide stays proportional regardless of available width/height.
    const updateBoardScale = useCallback(() => {
      const parent = boardRef.current?.parentElement;
      if (!parent) return;
      const pw = parent.clientWidth;
      const ph = parent.clientHeight;
      if (pw <= 0 || ph <= 0) return;
      let width = pw;
      let height = width / REFERENCE_RATIO;
      if (height > ph) {
        height = ph;
        width = height * REFERENCE_RATIO;
      }
      const EPS = 0.5;
      setBoardSize(prev => (Math.abs(prev.width - width) < EPS && Math.abs(prev.height - height) < EPS) ? prev : { width, height });
      setBoardScale(prev => {
        const next = width / REFERENCE_WIDTH;
        return Math.abs(prev - next) < 0.001 ? prev : next;
      });
    }, []);

    useLayoutEffect(() => {
      updateBoardScale();

      // Debounce via rAF: collapse bursts of observer callbacks into one
      // measurement per frame so a layout change triggered by setBoardSize
      // can't feed back into another synchronous setState.
      let rafId = null;
      const scheduleUpdate = () => {
        if (rafId != null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          updateBoardScale();
        });
      };
      const resizeObserver = new ResizeObserver(scheduleUpdate);
      const parent = boardRef.current?.parentElement;
      if (parent) {
        resizeObserver.observe(parent);
        // Also watch window resize so the board still tracks viewport changes.
        window.addEventListener('resize', scheduleUpdate);
      }

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', scheduleUpdate);
        if (rafId != null) cancelAnimationFrame(rafId);
      };
    }, [updateBoardScale]);

    // Re-measure after the mobile chat drawer opens/closes. The ResizeObserver
    // can miss the close transition, leaving the board frozen at the smaller
    // "chat open" size. useEffect (not useLayoutEffect) runs post-paint, once
    // the new layout is fully committed, so we read the correct parent size.
    useEffect(() => {
      updateBoardScale();
    }, [chatOpen, updateBoardScale]);

    // Helper to scale absolute values (font sizes, border radii, etc.)
    const scaleValue = useCallback((value) => {
      if (typeof value !== 'number') return value;
      return value * boardScale;
    }, [boardScale]);

    // Generate a consistent lesson session ID for syncing
    // Normalizes club lesson IDs so teacher and student use the same session ID
    const getLessonSessionId = (lesson) => {
      if (!lesson) return null;

      const idStr = lesson.id != null ? String(lesson.id) : null;
      // Check both camelCase and snake_case for source type (backend may return either)
      const sourceType = lesson.sourceType || lesson.source_type;

      // Handle club lessons - normalize to "club-X" format using club ID
      if (sourceType === 'club' || sourceType === 'club_session') {
        // Teacher has clubId directly, student may have it in different places
        const clubId = lesson.clubId || lesson.club_id || lesson.sourceClubId;

        // If we have a clubId, use it
        if (clubId) {
          return `club-${clubId}`;
        }

        // If lesson ID already in "club_X" or "club-X" format, normalize it
        if (idStr && (idStr.startsWith('club_') || idStr.startsWith('club-'))) {
          const clubIdFromString = idStr.replace(/^club[_-]/, '');
          return `club-${clubIdFromString}`;
        }

        // For student club lessons - use source_id (booking_id) as fallback
        // This will be handled by the backend to map to correct club
        const sourceId = lesson.sourceId || lesson.source_id;
        if (sourceId) {
          return `club-booking-${sourceId}`;
        }
      }

      // For non-club lessons with an ID
      if (idStr) {
        return idStr;
      }

      // Fallback: create deterministic ID from lesson properties
      const teacherId = lesson.teacherId || lesson.teacher_id || 'unknown';
      const studentId = lesson.studentId || lesson.student_id || 'unknown';
      const dateTime = lesson.dateTime || lesson.date_time || Date.now();
      return `session_${teacherId}_${studentId}_${new Date(dateTime).getTime()}`;
    };

    const lessonSessionId = getLessonSessionId(currentLessonForClass);

    // Teacher uses the presentation linked to this lesson. No fallback to
    // currentPresentation/presentations[0] — that masked "lesson has no
    // presentation" with the first slide of an unrelated deck.
    const lessonPresentation = currentLessonForClass?.presentationId
      ? presentations.find(p => p.id === currentLessonForClass.presentationId)
      : null;
    const teacherPresentation = lessonPresentation;

    // For students, use the synced presentation ID from the server
    const syncedPresentation = syncedPresentationId
      ? presentations.find(p => p.id === syncedPresentationId)
      : null;

    // Students use synced presentation when shared, teachers always use their local presentation
    const classPresentation = userRole === 'student' && presentationSharedFromStore && syncedPresentation
      ? syncedPresentation
      : teacherPresentation;

    // Use synced state for students, local state for teacher
    const effectivePresentationShared = userRole === 'student' ? presentationSharedFromStore : presentationShared;
    const effectiveSlideIndex = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
    const classSlide = classPresentation?.slides[effectiveSlideIndex];

    // Navigate via element slideLink action — teacher updates local index and syncs;
    // student just syncs (their view follows syncedSlideIndex).
    const handleSlideLinkClick = useCallback((element, e) => {
      const slideLink = element?.actions?.find(a => a.type === 'slideLink');
      if (!slideLink) return;
      const slides = classPresentation?.slides || [];
      const targetIndex = slides.findIndex(s => s.id === slideLink.targetSlideId);
      if (targetIndex < 0) return;
      e.stopPropagation();
      if (userRole === 'teacher') {
        setClassSlideIndex(targetIndex);
      }
      if (lessonSessionId) {
        updatePresentationState(lessonSessionId, {
          slideIndex: targetIndex,
          presentationId: classPresentation?.id
        });
      }
    }, [classPresentation, userRole, setClassSlideIndex, lessonSessionId, updatePresentationState]);

    // Teacher: follow server-synced slide index when it changes externally so
    // student-initiated slide-link navigations also move the teacher's view.
    // We track the previous synced value via ref so the teacher's own local
    // navigation (which writes to the server asynchronously) doesn't get
    // reverted by a transient stale read of this state.
    const prevSyncedSlideIndexRef = useRef(syncedSlideIndex);
    useEffect(() => {
      if (userRole !== 'teacher') return;
      if (typeof syncedSlideIndex !== 'number') return;
      if (syncedSlideIndex === prevSyncedSlideIndexRef.current) return;
      prevSyncedSlideIndexRef.current = syncedSlideIndex;
      setClassSlideIndex(syncedSlideIndex);
    }, [syncedSlideIndex, userRole, setClassSlideIndex]);

    // Handle interactive element actions - send to server for sync
    const handleWheelSpin = useCallback((elementId, result) => {
      // Update local state immediately for feedback
      setLocalInteractiveUI(prev => ({
        ...prev,
        wheelRotations: { ...prev.wheelRotations, [elementId]: result.rotation }
      }));

      // Track local interaction for conflict resolution
      trackLocalInteraction(elementId);

      // Calculate slide index based on role
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      // Send to server for sync with other participants
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'spinWheel', result.rotation, slideIdx, user?.id);
      }

      // Clear local interaction timestamp after delay
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleFlipCard = useCallback((elementId, isFlipped) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        flipStates: { ...prev.flipStates, [elementId]: isFlipped }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'flipCard', isFlipped, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleDiceRoll = useCallback((elementId, value) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        diceValues: { ...prev.diceValues, [elementId]: value }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'dice', value, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    // Alias game — full game state is one blob synced via interactive-state.
    // Both roles can push updates (explainer clicks Skip/Guessed; either can
    // dispatch turn-end when the timer expires). Server is last-write-wins.
    const handleAliasStateChange = useCallback((elementId, nextState) => {
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'aliasGame', nextState, slideIdx, user?.id);
      }
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState]);

    // Who Am I? game — teacher advances state; student mirrors.
    // Student may locally time out the timer, so both roles can push.
    const handleWhoAmIStateChange = useCallback((elementId, nextState) => {
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'whoAmIGame', nextState, slideIdx, user?.id);
      }
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState]);

    // Live-synced games (board game, spot the difference, mind map, dialogue,
    // pictionary). Each element keeps its own `rev` counter and ignores stale
    // echoes, so there's no local-interaction bookkeeping here — the state blob
    // is pushed as-is (last-write-wins on the server). `channelId` lets one
    // element use several independent blobs (pictionary strokes vs. game).
    const handleLiveGameStateChange = useCallback((channelId, elementType, nextState) => {
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, channelId, elementType, nextState, slideIdx, user?.id);
      }
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState]);

    const handleInputFieldChange = useCallback((elementId, value) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        inputValues: { ...prev.inputValues, [elementId]: value }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'inputField', value, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleNotebookChange = useCallback((elementId, value) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        inputValues: { ...prev.inputValues, [elementId]: value }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'notebook', value, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleCheckboxToggle = useCallback((elementId, checked) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        checkboxStates: { ...prev.checkboxStates, [elementId]: checked }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'checkbox', checked, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleLikeToggle = useCallback((elementId, liked) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        likeStates: { ...prev.likeStates, [elementId]: liked }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'like', liked, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleRatingChange = useCallback((elementId, rating) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        ratingStates: { ...prev.ratingStates, [elementId]: rating }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'rating', rating, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleShopPurchasedChange = useCallback((elementId, purchasedIds) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        shopStates: { ...prev.shopStates, [elementId]: purchasedIds }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'shop', purchasedIds, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleHighlightReveal = useCallback((elementId, revealed) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        highlightStates: { ...prev.highlightStates, [elementId]: revealed }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'highlight', revealed, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleCardDeckMovedChange = useCallback((elementId, movedIds) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        cardDeckStates: { ...prev.cardDeckStates, [elementId]: movedIds }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'cardDeck', movedIds, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleBookFlippedChange = useCallback((elementId, flippedCount) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        bookStates: { ...prev.bookStates, [elementId]: flippedCount }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'book', flippedCount, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleBingoCrossedChange = useCallback((elementId, crossedIds) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        bingoStates: { ...prev.bingoStates, [elementId]: crossedIds }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'bingo', crossedIds, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleKissMarryAvoidChange = useCallback((elementId, picks) => {
      setLocalInteractiveUI(prev => ({
        ...prev,
        kissMarryAvoidStates: { ...prev.kissMarryAvoidStates, [elementId]: picks }
      }));

      trackLocalInteraction(elementId);

      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'kissMarryAvoid', picks, slideIdx, user?.id);
      }

      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    // When the TEACHER clicks check on the student's answer, persist it to
    // exercise_results on the student's behalf. Without this the student view
    // (which hides the check button) would never register a graded attempt,
    // and the lesson's star calculation would come up empty.
    // Individual lessons only for now — the studentId comes from the lesson.
    const postTeacherGradeForStudent = useCallback((element, isCorrect, answerData, itemScore) => {
      if (userRole !== 'teacher') return;
      const lessonId = currentLessonForClass?.id;
      const studentId = currentLessonForClass?.studentId || currentLessonForClass?.student_id;
      if (!lessonId || !studentId || String(lessonId).startsWith('club')) return;
      const slideIdx = classSlideIndex;
      fetch(`${API.EXERCISES}/grade`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lessonId,
          presentationId: classPresentation?.id,
          slideIndex: slideIdx,
          elementId: element.id,
          studentId,
          exerciseType: element.type,
          isCorrect: !!isCorrect,
          answerData: answerData ?? null,
          ...(itemScore && Number.isFinite(itemScore.totalItems) && itemScore.totalItems > 0
            ? { earnedItems: itemScore.earnedItems, totalItems: itemScore.totalItems }
            : {}),
        }),
      }).catch((err) => console.error('Failed to grade student exercise:', err));
    }, [userRole, currentLessonForClass?.id, currentLessonForClass?.studentId, currentLessonForClass?.student_id, classPresentation?.id, classSlideIndex]);

    // Logs a client-computed exercise attempt to the backend for Anagram /
    // Boggle / WordPool / MatchLines / OddOneOut / TypeAnswer / FillGaps
    // (ExerciseElement and DragLabel have their own /submit endpoints).
    // - Student self-check → /attempt as themselves.
    // - Teacher-triggered check → /grade with the student's id, so the answer
    //   is credited to the student who actually gave it.
    // itemScore: optional { earnedItems, totalItems } so multi-item exercises
    // (matchLines pairs, sorting items, fillGaps gaps) score per-item instead
    // of as a binary all-or-nothing. Unjumble is intentionally binary (1 point
    // per correctly-ordered sentence), so it does not pass itemScore.
    const postExerciseAttempt = useCallback((element, isCorrect, answerData, itemScore) => {
      if (!lessonSessionId) return;
      if (userRole === 'student' && user?.id) {
        fetch(`${API.EXERCISES}/attempt`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lessonId: currentLessonForClass?.id,
            presentationId: classPresentation?.id,
            slideIndex: syncedSlideIndex,
            elementId: element.id,
            exerciseType: element.type,
            isCorrect: !!isCorrect,
            answerData: answerData ?? null,
            ...(itemScore && Number.isFinite(itemScore.totalItems) && itemScore.totalItems > 0
              ? { earnedItems: itemScore.earnedItems, totalItems: itemScore.totalItems }
              : {}),
          }),
        }).catch((err) => console.error('Failed to log exercise attempt:', err));
        return;
      }
      if (userRole === 'teacher') {
        postTeacherGradeForStudent(element, isCorrect, answerData, itemScore);
      }
    }, [userRole, lessonSessionId, user?.id, currentLessonForClass?.id, classPresentation?.id, syncedSlideIndex, postTeacherGradeForStudent]);

    // Exercise handlers
    const handleExerciseSelect = useCallback((elementId, selectedAnswers) => {
      const next = { selectedAnswers, showResult: false, isCorrect: null };
      setExerciseLocalState(prev => ({
        ...prev,
        [elementId]: {
          ...prev[elementId],
          ...next,
        }
      }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'exercise', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleExerciseCheck = useCallback(async (element, selectedAnswers) => {
      const elementId = element.id;
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      try {
        // Validate on server
        const response = await fetch(`${API.EXERCISES}/validate`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            presentationId: classPresentation?.id,
            slideIndex: slideIdx,
            elementId,
            selectedAnswers
          })
        });

        const result = await response.json();
        const nextState = {
          selectedAnswers,
          showResult: true,
          isCorrect: result.isCorrect,
          ...(Array.isArray(result.correctAnswers) ? { correctAnswers: result.correctAnswers } : {}),
        };

        // Update local state to show result
        setExerciseLocalState(prev => ({
          ...prev,
          [elementId]: {
            ...prev[elementId],
            ...nextState,
          }
        }));

        trackLocalInteraction(elementId);
        // Sync result across roles (teacher reveal → student sees it; student self-check → teacher sees it)
        if (lessonSessionId) {
          sendInteractiveState(lessonSessionId, elementId, 'exercise', nextState, slideIdx, user?.id);
        }
        setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);

        // Persist student grade only when the STUDENT checks their own answer
        if (userRole === 'student' && lessonSessionId && user?.id) {
          // Submit to database
          fetch(`${API.EXERCISES}/submit`, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              lessonId: currentLessonForClass?.id,
              presentationId: classPresentation?.id,
              slideIndex: slideIdx,
              elementId,
              selectedAnswers,
              studentId: user.id,
              exerciseType: element.type
            })
          });

          // Legacy: teacher-visibility endpoint (kept for backward compat)
          fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/exercise-answer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              elementId,
              studentId: user.id,
              studentName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Student',
              selectedAnswers,
              isCorrect: result.isCorrect,
              attemptNumber: 1,
              slideIndex: slideIdx
            })
          });
        }

        // Teacher-triggered check: record the student's answer on their behalf.
        // Individual lessons only — currentLessonForClass has the studentId.
        if (userRole === 'teacher') {
          postTeacherGradeForStudent(element, result.isCorrect, selectedAnswers);
        }
      } catch (error) {
        console.error('Error checking exercise:', error);
      }
    }, [userRole, syncedSlideIndex, classSlideIndex, classPresentation?.id, lessonSessionId, user, currentLessonForClass?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postTeacherGradeForStudent]);

    const handleExerciseRetry = useCallback((elementId) => {
      const next = { selectedAnswers: [], showResult: false, isCorrect: null };
      setExerciseLocalState(prev => ({
        ...prev,
        [elementId]: next
      }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'exercise', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    // Effective choice-exercise state: recent local overrides synced, then sync, then local fallback
    const getExerciseState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && exerciseLocalState[elementId]) {
        return exerciseLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || exerciseLocalState[elementId] || {};
    }, [exerciseLocalState, syncedInteractiveStates]);

    // Anagram handlers
    const handleAnagramArrangementChange = useCallback((elementId, arrangement) => {
      const next = { arrangement, showResult: false, isCorrect: null };
      setAnagramLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'anagram', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleAnagramCheck = useCallback((element, currentWord) => {
      const elementId = element.id;
      const correctWord = (element.content?.word || '').toUpperCase();
      const isCorrect = currentWord === correctWord;
      // When the teacher initiates the check, `anagramLocalState` on their side
      // has no arrangement (only the student dragged) — pull it from the synced
      // state so we don't broadcast `arrangement: null` back to the student and
      // reset their tiles to the seeded scrambled order.
      const syncedArrangement = syncedInteractiveStates[elementId]?.state?.arrangement || null;
      const arrangement = anagramLocalState[elementId]?.arrangement || syncedArrangement;
      setAnagramLocalState(prev => ({
        ...prev,
        [elementId]: { arrangement, showResult: true, isCorrect },
      }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(
          lessonSessionId,
          elementId,
          'anagram',
          { arrangement, showResult: true, isCorrect },
          slideIdx,
          user?.id
        );
      }
      postExerciseAttempt(element, isCorrect, { word: currentWord });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, anagramLocalState, syncedInteractiveStates, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleAnagramRetry = useCallback((elementId) => {
      const next = { arrangement: null, showResult: false, isCorrect: null };
      setAnagramLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'anagram', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    // Effective anagram state: prefer recent local, otherwise synced
    const getAnagramState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && anagramLocalState[elementId]) {
        return anagramLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || anagramLocalState[elementId] || {};
    }, [anagramLocalState, syncedInteractiveStates]);

    // Boggle handlers
    const handleBoggleStateChange = useCallback((elementId, partial) => {
      const next = { ...partial, showResult: false, isCorrect: null };
      setBoggleLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'boggle', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleBoggleCheck = useCallback((element, result) => {
      const elementId = element.id;
      const mode = element.content?.mode || 'targets';
      const foundWords = result.foundWords || [];
      const targets = (element.content?.targetWords || []).map(w => (w || '').toUpperCase().replace(/[^A-ZА-ЯІЇЄҐ]/gi, '')).filter(Boolean);
      const validFound = mode === 'time'
        ? foundWords.length
        : foundWords.filter(w => targets.includes(w)).length;
      let earnedItems;
      let totalItems;
      if (element.type === 'boggleExercise') {
        // Wordsearch: 1 star per valid found word, capped at 5.
        totalItems = Math.min(5, mode === 'time' ? 5 : Math.max(1, targets.length));
        earnedItems = Math.min(validFound, totalItems);
      } else {
        // WordPool (boggle): tiered star thresholds picked by the admin.
        const thresholds = Array.isArray(element.content?.starThresholds) && element.content.starThresholds.length === 5
          ? element.content.starThresholds
          : [1, 2, 5, 8, 11];
        earnedItems = 0;
        for (let tier = 5; tier >= 1; tier--) {
          if (validFound >= thresholds[tier - 1]) { earnedItems = tier; break; }
        }
        totalItems = 5;
      }
      let isCorrect;
      if (mode === 'time') {
        const minWords = element.content?.minWordsToPass ?? 1;
        isCorrect = foundWords.length >= minWords;
      } else {
        isCorrect = targets.length > 0 && validFound === targets.length;
      }
      setBoggleLocalState(prev => {
        const prevState = prev[elementId] || {};
        const next = { ...prevState, foundWords: foundWords.length ? foundWords : (prevState.foundWords || []), showResult: true, isCorrect };
        return { ...prev, [elementId]: next };
      });
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        const prevState = boggleLocalState[elementId] || {};
        sendInteractiveState(
          lessonSessionId,
          elementId,
          'boggle',
          { ...prevState, foundWords: result.foundWords || prevState.foundWords || [], showResult: true, isCorrect },
          slideIdx,
          user?.id
        );
      }
      postExerciseAttempt(element, isCorrect, { foundWords }, { earnedItems, totalItems });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, boggleLocalState, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleBoggleRetry = useCallback((elementId) => {
      const next = { path: [], foundWords: [], startedAt: 0, showResult: false, isCorrect: null };
      setBoggleLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'boggle', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getBoggleState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && boggleLocalState[elementId]) {
        return boggleLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || boggleLocalState[elementId] || {};
    }, [boggleLocalState, syncedInteractiveStates]);

    // Drag-label exercise handlers (image + text)
    const handleDragLabelPlacementsChange = useCallback((elementId, placements) => {
      const next = { placements, showResult: false, perSlotResults: null };
      setDragLabelLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'dragLabel', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleDragLabelCheck = useCallback(async (element, placements) => {
      const elementId = element.id;
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;

      try {
        const response = await fetch(`${API.EXERCISES}/validate-drag-label`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            presentationId: classPresentation?.id,
            slideIndex: slideIdx,
            elementId,
            placements
          })
        });
        const result = await response.json();
        const perSlotResults = result.perSlotResults || {};
        const isCorrect = result.isCorrect;
        const next = { placements, showResult: true, perSlotResults, isCorrect };
        setDragLabelLocalState(prev => ({ ...prev, [elementId]: next }));
        trackLocalInteraction(elementId);
        if (lessonSessionId) {
          sendInteractiveState(lessonSessionId, elementId, 'dragLabel', next, slideIdx, user?.id);
          // Student self-check: persist to exercise_results as themselves.
          if (userRole === 'student' && user?.id) {
            fetch(`${API.EXERCISES}/submit-drag-label`, {
              credentials: 'include',
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                lessonId: currentLessonForClass?.id,
                presentationId: classPresentation?.id,
                slideIndex: slideIdx,
                elementId,
                placements,
                studentId: user.id,
                exerciseType: element.type
              })
            });
            fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/exercise-answer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                elementId,
                studentId: user.id,
                studentName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Student',
                selectedAnswers: placements,
                isCorrect,
                attemptNumber: 1,
                slideIndex: slideIdx
              })
            });
          }
        }
        // Teacher-triggered check on the student's placements.
        if (userRole === 'teacher') {
          const totalItems = Object.keys(perSlotResults).length;
          const earnedItems = Object.values(perSlotResults).filter(Boolean).length;
          const itemScore = totalItems > 0 ? { earnedItems, totalItems } : undefined;
          postTeacherGradeForStudent(element, isCorrect, placements, itemScore);
        }
        setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
      } catch (e) {
        console.error('Error validating drag-label exercise:', e);
      }
    }, [userRole, syncedSlideIndex, classSlideIndex, classPresentation?.id, lessonSessionId, user, currentLessonForClass?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postTeacherGradeForStudent]);

    const handleDragLabelRetry = useCallback((elementId) => {
      const next = { placements: {}, showResult: false, perSlotResults: null };
      setDragLabelLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'dragLabel', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getDragLabelState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && dragLabelLocalState[elementId]) {
        return dragLabelLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || dragLabelLocalState[elementId] || {};
    }, [dragLabelLocalState, syncedInteractiveStates]);

    // Match-lines exercise handlers (client-side validation for now)
    const handleMatchLinesConnectionsChange = useCallback((elementId, connections) => {
      const next = { connections, showResult: false, perPairResults: null };
      setMatchLinesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'matchLines', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleMatchLinesCheck = useCallback((element, connections) => {
      const elementId = element.id;
      const pairs = element.content?.pairs || [];
      const perPairResults = {};
      pairs.forEach((p) => {
        perPairResults[p.id] = connections[p.id] === p.id;
      });
      const isCorrect = pairs.length > 0 && Object.values(perPairResults).every(v => v === true);
      const next = { connections, showResult: true, perPairResults, isCorrect };
      setMatchLinesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'matchLines', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { connections }, {
        earnedItems: Object.values(perPairResults).filter(Boolean).length,
        totalItems: pairs.length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleMatchLinesRetry = useCallback((elementId) => {
      const next = { connections: {}, showResult: false, perPairResults: null };
      setMatchLinesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'matchLines', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getMatchLinesState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && matchLinesLocalState[elementId]) {
        return matchLinesLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || matchLinesLocalState[elementId] || {};
    }, [matchLinesLocalState, syncedInteractiveStates]);

    // Label-image exercise handlers (client-side validation for now)
    const handleLabelImageConnectionsChange = useCallback((elementId, connections) => {
      const next = { connections, showResult: false, perItemResults: null };
      setLabelImageLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'labelImage', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleLabelImageCheck = useCallback((element, connections) => {
      const elementId = element.id;
      const items = element.content?.items || [];
      const perItemResults = {};
      items.forEach((it) => {
        perItemResults[it.id] = connections[it.id] === it.id;
      });
      const isCorrect = items.length > 0 && Object.values(perItemResults).every(v => v === true);
      const next = { connections, showResult: true, perItemResults, isCorrect };
      setLabelImageLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'labelImage', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { connections }, {
        earnedItems: Object.values(perItemResults).filter(Boolean).length,
        totalItems: items.length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleLabelImageRetry = useCallback((elementId) => {
      const next = { connections: {}, showResult: false, perItemResults: null };
      setLabelImageLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'labelImage', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getLabelImageState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && labelImageLocalState[elementId]) {
        return labelImageLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || labelImageLocalState[elementId] || {};
    }, [labelImageLocalState, syncedInteractiveStates]);

    // Find-sticker exercise: continuous progression — each correct click
    // reveals the next word. Sync `foundIds` + `idx` so the teacher's mirrored
    // view shows the same stickers vanishing and the same prompt word.
    const handleFindStickerChange = useCallback((element, next) => {
      const elementId = element.id;
      setFindStickerLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'findSticker', next, slideIdx, user?.id);
      }
      const totalStickers = (element.content?.stickers || [])
        .filter(s => s.src && typeof s.x === 'number' && typeof s.y === 'number' && (s.word || '').trim())
        .length;
      const done = totalStickers > 0 && (next.foundIds?.length || 0) >= totalStickers;
      if (done) {
        postExerciseAttempt(element, true, { foundIds: next.foundIds }, {
          earnedItems: totalStickers,
          totalItems: totalStickers,
        });
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleFindStickerRetry = useCallback((elementId) => {
      const next = { foundIds: [], idx: 0 };
      setFindStickerLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'findSticker', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getFindStickerState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && findStickerLocalState[elementId]) {
        return findStickerLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || findStickerLocalState[elementId] || {};
    }, [findStickerLocalState, syncedInteractiveStates]);

    // Odd-one-out exercise handlers (client-side validation)
    const handleOddOneOutSelectionsChange = useCallback((elementId, selections) => {
      const next = { selections, showResult: false, perRowResults: null };
      setOddOneOutLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'oddOneOut', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleOddOneOutCheck = useCallback((element, selections) => {
      const elementId = element.id;
      const rows = element.content?.rows || [];
      const perRowResults = {};
      rows.forEach((r) => {
        perRowResults[r.id] = {
          isCorrect: selections?.[r.id] === r.oddId,
          correctId: r.oddId,
          explanation: r.explanation || '',
        };
      });
      const isCorrect = rows.length > 0 && Object.values(perRowResults).every(v => v.isCorrect);
      const next = { selections, showResult: true, perRowResults, isCorrect };
      setOddOneOutLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'oddOneOut', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { selections }, {
        earnedItems: Object.values(perRowResults).filter(v => v.isCorrect).length,
        totalItems: rows.length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleOddOneOutRetry = useCallback((elementId) => {
      const next = { selections: {}, showResult: false, perRowResults: null };
      setOddOneOutLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'oddOneOut', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getOddOneOutState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && oddOneOutLocalState[elementId]) {
        return oddOneOutLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || oddOneOutLocalState[elementId] || {};
    }, [oddOneOutLocalState, syncedInteractiveStates]);

    // Sorting exercise handlers (client-side validation)
    const handleSortingPlacementsChange = useCallback((elementId, placements) => {
      const next = { placements, showResult: false, perItemResults: null };
      setSortingLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'sorting', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleSortingCheck = useCallback((element, placements) => {
      const elementId = element.id;
      const items = element.content?.items || [];
      const perItemResults = {};
      let correctCount = 0;
      items.forEach((it) => {
        const isCorrect = placements?.[it.id] === it.categoryId;
        perItemResults[it.id] = isCorrect;
        if (isCorrect) correctCount += 1;
      });
      const isCorrect = items.length > 0 && correctCount === items.length;
      const next = { placements, showResult: true, perItemResults, isCorrect };
      setSortingLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'sorting', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { placements }, {
        earnedItems: correctCount,
        totalItems: items.length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleSortingRetry = useCallback((elementId) => {
      const next = { placements: {}, showResult: false, perItemResults: null };
      setSortingLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'sorting', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getSortingState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && sortingLocalState[elementId]) {
        return sortingLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || sortingLocalState[elementId] || {};
    }, [sortingLocalState, syncedInteractiveStates]);

    // Unjumble (sentence order) handlers
    const handleUnjumbleArrangementChange = useCallback((elementId, arrangement) => {
      const next = { arrangement, showResult: false, perWordResults: null, isCorrect: null };
      setUnjumbleLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'unjumble', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleUnjumbleCheck = useCallback((element, arrangement) => {
      const elementId = element.id;
      // Normalize legacy flat Tile[] arrangements to Tile[][].
      const rows = (Array.isArray(arrangement) && arrangement.length > 0 && !Array.isArray(arrangement[0]))
        ? [arrangement]
        : (arrangement || []);
      const {
        perWordResults,
        isCorrect,
        correctSentences,
        totalSentences,
      } = validateUnjumble(element.content, rows);
      const next = { arrangement: rows, showResult: true, perWordResults, isCorrect };
      setUnjumbleLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'unjumble', next, slideIdx, user?.id);
      }
      // 1 point per correctly-ordered sentence.
      const answerData = { arrangement: rows.map((row) => (row || []).map((t) => t?.word)) };
      if (totalSentences > 1) {
        postExerciseAttempt(element, isCorrect, answerData, { earnedItems: correctSentences, totalItems: totalSentences });
      } else {
        postExerciseAttempt(element, isCorrect, answerData);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleUnjumbleRetry = useCallback((elementId) => {
      const next = { arrangement: null, showResult: false, perWordResults: null, isCorrect: null };
      setUnjumbleLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'unjumble', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getUnjumbleState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && unjumbleLocalState[elementId]) {
        return unjumbleLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || unjumbleLocalState[elementId] || {};
    }, [unjumbleLocalState, syncedInteractiveStates]);

    // Order-sentences handlers
    const handleOrderSentencesArrangementChange = useCallback((elementId, arrangement) => {
      const next = { arrangement, showResult: false, perItemResults: null, isCorrect: null };
      setOrderSentencesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'orderSentences', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleOrderSentencesCheck = useCallback((element, arrangement) => {
      const elementId = element.id;
      const tiles = Array.isArray(arrangement) ? arrangement : [];
      const { perItemResults, isCorrect, correctItems, totalItems } = validateOrderSentences(element.content, tiles);
      const next = { arrangement: tiles, showResult: true, perItemResults, isCorrect };
      setOrderSentencesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'orderSentences', next, slideIdx, user?.id);
      }
      const answerData = { arrangement: tiles.map((t) => t?.text) };
      postExerciseAttempt(element, isCorrect, answerData, { earnedItems: correctItems, totalItems });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleOrderSentencesRetry = useCallback((elementId) => {
      const next = { arrangement: null, showResult: false, perItemResults: null, isCorrect: null };
      setOrderSentencesLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'orderSentences', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getOrderSentencesState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && orderSentencesLocalState[elementId]) {
        return orderSentencesLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || orderSentencesLocalState[elementId] || {};
    }, [orderSentencesLocalState, syncedInteractiveStates]);

    // Rank (order from first to last) handlers
    const handleRankPlacementsChange = useCallback((elementId, placements) => {
      const next = { placements, showResult: false, perItemResults: null };
      setRankLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'rank', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleRankCheck = useCallback((element, placements) => {
      const elementId = element.id;
      const items = element.content?.items || [];
      const { perItemResults, correctCount, isCorrect } = validateRank(items, placements);
      const next = { placements, showResult: true, perItemResults, isCorrect };
      setRankLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'rank', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { placements }, {
        earnedItems: correctCount,
        totalItems: items.length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleRankRetry = useCallback((elementId) => {
      const next = { placements: {}, showResult: false, perItemResults: null };
      setRankLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'rank', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getRankState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && rankLocalState[elementId]) {
        return rankLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || rankLocalState[elementId] || {};
    }, [rankLocalState, syncedInteractiveStates]);

    // Type-answer exercise handlers (client-side validation)
    const handleTypeAnswerChange = useCallback((elementId, answer) => {
      const next = { answer, showResult: false, isCorrect: null };
      setTypeAnswerLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'typeAnswer', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleTypeAnswerCheck = useCallback((element, answer) => {
      const elementId = element.id;
      const correctAnswers = element.content?.correctAnswers || [];
      const normalize = (s) => (s || '')
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/[.,!?;:"'’‘“”()\[\]{}\-–—]/g, '');
      const userVal = normalize(answer);
      const isCorrect = userVal.length > 0 && correctAnswers.some((a) => normalize(a) === userVal);
      const next = { answer, showResult: true, isCorrect };
      setTypeAnswerLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'typeAnswer', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answer });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleTypeAnswerRetry = useCallback((elementId) => {
      const next = { answer: '', showResult: false, isCorrect: null };
      setTypeAnswerLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'typeAnswer', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getTypeAnswerState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && typeAnswerLocalState[elementId]) {
        return typeAnswerLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || typeAnswerLocalState[elementId] || {};
    }, [typeAnswerLocalState, syncedInteractiveStates]);

    // Missing-letters exercise handlers (client-side validation)
    const handleMissingLettersChange = useCallback((elementId, answer) => {
      const next = { answer, showResult: false, isCorrect: null, perLetterResults: null };
      setMissingLettersLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'missingLetters', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleMissingLettersCheck = useCallback((element, answer) => {
      const elementId = element.id;
      const word = element.content?.word || '';
      const missingIndices = element.content?.missingIndices || [];
      const { isCorrect, perLetterResults } = gradeMissingLetters(word, missingIndices, answer);
      const next = { answer, showResult: true, isCorrect, perLetterResults };
      setMissingLettersLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'missingLetters', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answer });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleMissingLettersRetry = useCallback((elementId) => {
      const next = { answer: {}, showResult: false, isCorrect: null, perLetterResults: null };
      setMissingLettersLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'missingLetters', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getMissingLettersState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && missingLettersLocalState[elementId]) {
        return missingLettersLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || missingLettersLocalState[elementId] || {};
    }, [missingLettersLocalState, syncedInteractiveStates]);

    // True/False exercise handlers (client-side validation)
    const handleTrueFalseAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perStatementResults: null, isCorrect: null };
      setTrueFalseLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'trueFalse', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleTrueFalseCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const statements = element.content?.statements || [];
      const allowNoInfo = !!element.content?.allowNoInfo;
      const letters = element.content?.letters;
      const perStatementResults = {};
      statements.forEach((s) => {
        const given = normalizeTrueFalseLetter(answers?.[s.id], { letters, allowNoInfo });
        perStatementResults[s.id] = !!given && given === s.correctAnswer;
      });
      const isCorrect = statements.length > 0 && Object.values(perStatementResults).every(v => v === true);
      const next = { answers, showResult: true, perStatementResults, isCorrect };
      setTrueFalseLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'trueFalse', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleTrueFalseRetry = useCallback((elementId) => {
      const next = { answers: {}, showResult: false, perStatementResults: null, isCorrect: null };
      setTrueFalseLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'trueFalse', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getTrueFalseState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && trueFalseLocalState[elementId]) {
        return trueFalseLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || trueFalseLocalState[elementId] || {};
    }, [trueFalseLocalState, syncedInteractiveStates]);

    // ABCD exercise handlers (client-side validation)
    const handleAbcdAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perQuestionResults: null, isCorrect: null };
      setAbcdLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'abcd', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleAbcdCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const questions = element.content?.questions || [];
      const { perQuestionResults, isCorrect, earnedItems, totalItems } = gradeAbcd(questions, answers);
      const next = { answers, showResult: true, perQuestionResults, isCorrect };
      setAbcdLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'abcd', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers }, { earnedItems, totalItems });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const getAbcdState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && abcdLocalState[elementId]) {
        return abcdLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || abcdLocalState[elementId] || {};
    }, [abcdLocalState, syncedInteractiveStates]);

    // Choose-word exercise handlers (client-side validation)
    const handleChooseWordAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perChoiceResults: null, isCorrect: null };
      setChooseWordLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'chooseWord', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleChooseWordCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const { perChoiceResults, isCorrect } = gradeChooseWord(element, answers);
      const next = { answers, showResult: true, perChoiceResults, isCorrect };
      setChooseWordLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'chooseWord', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleChooseWordRetry = useCallback((elementId) => {
      const next = { answers: {}, showResult: false, perChoiceResults: null, isCorrect: null };
      setChooseWordLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'chooseWord', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getChooseWordState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && chooseWordLocalState[elementId]) {
        return chooseWordLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || chooseWordLocalState[elementId] || {};
    }, [chooseWordLocalState, syncedInteractiveStates]);

    // Fix-mistake exercise handlers (client-side validation)
    const handleFixMistakeAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perMistakeResults: null, isCorrect: null };
      setFixMistakeLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fixMistake', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleFixMistakeCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const { perMistakeResults, isCorrect } = gradeFixMistake(element, answers);
      const next = { answers, showResult: true, perMistakeResults, isCorrect };
      setFixMistakeLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fixMistake', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleFixMistakeRetry = useCallback((elementId) => {
      const next = { answers: {}, showResult: false, perMistakeResults: null, isCorrect: null };
      setFixMistakeLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fixMistake', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getFixMistakeState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && fixMistakeLocalState[elementId]) {
        return fixMistakeLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || fixMistakeLocalState[elementId] || {};
    }, [fixMistakeLocalState, syncedInteractiveStates]);

    // Pronounce (speak-into-mic) exercise handlers.
    // Star scoring lives in PronounceElement.js (computePronounceScore +
    // scoreToStars); we re-compute here so postExerciseAttempt sees the
    // same earnedItems the student sees in the element badge.
    const handlePronounceStateChange = useCallback((elementId, patch) => {
      const next = { ...(pronounceLocalState[elementId] || {}), ...patch, showResult: false, isCorrect: null };
      setPronounceLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'pronounce', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [pronounceLocalState, lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handlePronounceCheck = useCallback((element, result) => {
      const elementId = element.id;
      const target = element.content?.targetText || '';
      const thresholds = element.content?.starThresholds;
      const transcript = result.transcript || '';
      const confidence = result.confidence ?? null;
      const score = computePronounceScore(transcript, target, confidence);
      const earnedItems = scoreToStars(score, thresholds);
      const totalItems = 5;
      const isCorrect = earnedItems >= 3;
      const next = { transcript, confidence, showResult: true, isCorrect };
      setPronounceLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'pronounce', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { transcript, confidence }, { earnedItems, totalItems });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handlePronounceRetry = useCallback((elementId) => {
      const next = { transcript: '', confidence: null, showResult: false, isCorrect: null };
      setPronounceLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'pronounce', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getPronounceState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && pronounceLocalState[elementId]) {
        return pronounceLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || pronounceLocalState[elementId] || {};
    }, [pronounceLocalState, syncedInteractiveStates]);

    // Fill-gaps exercise handlers (client-side validation)
    const handleFillGapsAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perGapResults: null, isCorrect: null };
      setFillGapsLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fillGaps', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleFillGapsCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const { perGapResults, isCorrect } = computeFillGapsResults(element.content, answers);
      const totalItems = Object.keys(perGapResults).length;
      const next = { answers, showResult: true, perGapResults, isCorrect };
      setFillGapsLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fillGaps', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers }, {
        earnedItems: Object.values(perGapResults).filter(Boolean).length,
        totalItems,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleFillGapsRetry = useCallback((elementId) => {
      const next = { answers: {}, showResult: false, perGapResults: null, isCorrect: null };
      setFillGapsLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'fillGaps', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getFillGapsState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && fillGapsLocalState[elementId]) {
        return fillGapsLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || fillGapsLocalState[elementId] || {};
    }, [fillGapsLocalState, syncedInteractiveStates]);

    // Table-fill exercise handlers (client-side validation, per-cell scoring)
    const handleTableFillAnswersChange = useCallback((elementId, answers) => {
      const next = { answers, showResult: false, perCellResults: null, isCorrect: null };
      setTableFillLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'tableFill', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const handleTableFillCheck = useCallback((element, answers) => {
      const elementId = element.id;
      const { perCellResults, isCorrect } = computeTableFillResults(element.content, answers);
      const next = { answers, showResult: true, perCellResults, isCorrect };
      setTableFillLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'tableFill', next, slideIdx, user?.id);
      }
      postExerciseAttempt(element, isCorrect, { answers }, {
        earnedItems: Object.values(perCellResults).filter(Boolean).length,
        totalItems: Object.keys(perCellResults).length,
      });
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp, postExerciseAttempt]);

    const handleTableFillRetry = useCallback((elementId) => {
      const next = { answers: {}, showResult: false, perCellResults: null, isCorrect: null };
      setTableFillLocalState(prev => ({ ...prev, [elementId]: next }));
      trackLocalInteraction(elementId);
      const slideIdx = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
      if (lessonSessionId) {
        sendInteractiveState(lessonSessionId, elementId, 'tableFill', next, slideIdx, user?.id);
      }
      setTimeout(() => clearLocalInteractionTimestamp(elementId), 1000);
    }, [lessonSessionId, userRole, syncedSlideIndex, classSlideIndex, user?.id, sendInteractiveState, trackLocalInteraction, clearLocalInteractionTimestamp]);

    const getTableFillState = useCallback((elementId) => {
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);
      if (isLocalRecent && tableFillLocalState[elementId]) {
        return tableFillLocalState[elementId];
      }
      return syncedInteractiveStates[elementId]?.state || tableFillLocalState[elementId] || {};
    }, [tableFillLocalState, syncedInteractiveStates]);

    // Teacher: check every interactive exercise on the current slide at once.
    // Reveals results (green/red) using each student's currently-entered answer.
    // Games with built-in auto-check (sortingGame, listenTypeGame) intentionally
    // skipped — they check themselves when the student finishes.
    const handleCheckAllOnSlide = useCallback(() => {
      const elements = classSlide?.elements || [];
      // If a drag-label has slots but the teacher's synced state has no
      // placements, we surface a hint instead of silently skipping — that
      // silent skip made "Перевірити" look broken when the student's
      // placements sync hadn't reached the teacher's browser yet.
      let warnedDragLabelEmpty = false;
      elements.forEach((el) => {
        switch (el.type) {
          case 'singleChoiceExercise':
          case 'multipleChoiceExercise': {
            const state = getExerciseState(el.id);
            if (state.selectedAnswers?.length > 0 && !state.showResult) {
              handleExerciseCheck(el, state.selectedAnswers);
            }
            break;
          }
          case 'anagramExercise': {
            const state = getAnagramState(el.id);
            const arrangement = state.arrangement || [];
            const word = arrangement.map((t) => t?.letter || '').join('');
            if (word && !state.showResult) handleAnagramCheck(el, word);
            break;
          }
          case 'boggleExercise':
          case 'wordPoolExercise': {
            const state = getBoggleState(el.id);
            if (!state.showResult) handleBoggleCheck(el, { foundWords: state.foundWords || [] });
            break;
          }
          case 'dragLabelImageExercise':
          case 'dragLabelTextExercise':
          case 'dragLabelBlankExercise': {
            const state = getDragLabelState(el.id);
            if (state.showResult) break;
            const slotCount = Array.isArray(el.content?.slots) ? el.content.slots.length : 0;
            const placementCount = state.placements ? Object.keys(state.placements).length : 0;
            if (slotCount > 0 && placementCount === 0) {
              if (!warnedDragLabelEmpty) {
                toast(tt('checkPending'));
                warnedDragLabelEmpty = true;
              }
              break;
            }
            if (state.placements) handleDragLabelCheck(el, state.placements);
            break;
          }
          case 'matchLinesExercise': {
            const state = getMatchLinesState(el.id);
            if (state.connections && !state.showResult) handleMatchLinesCheck(el, state.connections);
            break;
          }
          case 'labelImageExercise': {
            const state = getLabelImageState(el.id);
            if (state.connections && !state.showResult) handleLabelImageCheck(el, state.connections);
            break;
          }
          case 'oddOneOutExercise': {
            const state = getOddOneOutState(el.id);
            if (state.selections && Object.keys(state.selections).length > 0 && !state.showResult) {
              handleOddOneOutCheck(el, state.selections);
            }
            break;
          }
          case 'sortingExercise': {
            const state = getSortingState(el.id);
            if (state.placements && Object.keys(state.placements).length > 0 && !state.showResult) {
              handleSortingCheck(el, state.placements);
            }
            break;
          }
          case 'unjumbleExercise': {
            const state = getUnjumbleState(el.id);
            const sentences = getUnjumbleSentences(el.content);
            if (
              !state.showResult
              && Array.isArray(state.arrangement)
              && state.arrangement.length === sentences.length
              && state.arrangement.every((row) => Array.isArray(row) && row.length > 0)
            ) {
              handleUnjumbleCheck(el, state.arrangement);
            }
            break;
          }
          case 'orderSentencesExercise': {
            const state = getOrderSentencesState(el.id);
            const sentences = getOrderSentencesFromContent(el.content);
            if (
              !state.showResult
              && Array.isArray(state.arrangement)
              && state.arrangement.length === sentences.length
            ) {
              handleOrderSentencesCheck(el, state.arrangement);
            }
            break;
          }
          case 'rankExercise': {
            const state = getRankState(el.id);
            const items = el.content?.items || [];
            const placements = state.placements || {};
            const allPlaced = items.length > 0 && items.every((it) => placements[it.id] !== undefined && placements[it.id] !== null);
            if (allPlaced && !state.showResult) {
              handleRankCheck(el, placements);
            }
            break;
          }
          case 'typeAnswerExercise': {
            const state = getTypeAnswerState(el.id);
            if ((state.answer || '').trim() && !state.showResult) handleTypeAnswerCheck(el, state.answer);
            break;
          }
          case 'missingLettersExercise': {
            const state = getMissingLettersState(el.id);
            const answer = state.answer || {};
            const missingIndices = el.content?.missingIndices || [];
            const allFilled = missingIndices.length > 0
              && missingIndices.every((idx) => ((answer[idx] || '').toString().trim().length > 0));
            if (allFilled && !state.showResult) handleMissingLettersCheck(el, answer);
            break;
          }
          case 'trueFalseExercise': {
            const state = getTrueFalseState(el.id);
            const statements = el.content?.statements || [];
            const allowNoInfo = !!el.content?.allowNoInfo;
            const letters = el.content?.letters;
            const answers = state.answers || {};
            const allFilled = statements.length > 0
              && statements.every(s => !!normalizeTrueFalseLetter(answers[s.id], { letters, allowNoInfo }));
            if (allFilled && !state.showResult) handleTrueFalseCheck(el, answers);
            break;
          }
          case 'abcdExercise': {
            const state = getAbcdState(el.id);
            const questions = el.content?.questions || [];
            const answers = state.answers || {};
            if (questions.some((q) => !!answers[q.id]) && !state.showResult) handleAbcdCheck(el, answers);
            break;
          }
          case 'chooseWordExercise': {
            const state = getChooseWordState(el.id);
            const sents = el.content?.sentences || [];
            const answers = state.answers || {};
            const allFilled = sents.length > 0 && sents.every((s) => {
              const { choiceCount } = parseChooseWordSentence(s.text || '');
              if (choiceCount === 0) return true;
              const arr = answers[s.id] || [];
              for (let i = 0; i < choiceCount; i += 1) {
                if (arr[i] !== 0 && arr[i] !== 1) return false;
              }
              return true;
            });
            if (allFilled && !state.showResult) handleChooseWordCheck(el, answers);
            break;
          }
          case 'fixMistakeExercise': {
            const state = getFixMistakeState(el.id);
            const sents = el.content?.sentences || [];
            const answers = state.answers || {};
            const allFilled = sents.length > 0 && sents.every((s) => {
              const { mistakeCount } = parseFixMistakeSentence(s.text || '');
              if (mistakeCount === 0) return true;
              const bag = answers[s.id] || {};
              for (let i = 0; i < mistakeCount; i += 1) {
                const v = (bag[i] ?? '').toString().trim();
                if (!v) return false;
              }
              return true;
            });
            if (allFilled && !state.showResult) handleFixMistakeCheck(el, answers);
            break;
          }
          case 'fillGapsExercise': {
            const state = getFillGapsState(el.id);
            if (state.answers && !state.showResult) handleFillGapsCheck(el, state.answers);
            break;
          }
          case 'tableFillExercise': {
            const state = getTableFillState(el.id);
            if (state.answers && !state.showResult) handleTableFillCheck(el, state.answers);
            break;
          }
          case 'pronounceExercise': {
            const state = getPronounceState(el.id);
            if ((state.transcript || '').trim() && !state.showResult) {
              handlePronounceCheck(el, { transcript: state.transcript, confidence: state.confidence });
            }
            break;
          }
          default:
            break;
        }
      });
    }, [classSlide, getExerciseState, getAnagramState, getBoggleState, getDragLabelState, getMatchLinesState, getLabelImageState, getOddOneOutState, getSortingState, getUnjumbleState, getOrderSentencesState, getRankState, getTypeAnswerState, getTrueFalseState, getAbcdState, getChooseWordState, getFixMistakeState, getFillGapsState, getTableFillState, getPronounceState, getMissingLettersState, handleExerciseCheck, handleAnagramCheck, handleBoggleCheck, handleDragLabelCheck, handleMatchLinesCheck, handleLabelImageCheck, handleOddOneOutCheck, handleSortingCheck, handleUnjumbleCheck, handleOrderSentencesCheck, handleRankCheck, handleTypeAnswerCheck, handleTrueFalseCheck, handleAbcdCheck, handleChooseWordCheck, handleFixMistakeCheck, handleFillGapsCheck, handleTableFillCheck, handlePronounceCheck, handleMissingLettersCheck]);

    // True if the current slide has at least one exercise the teacher can check
    const slideHasCheckable = useMemo(() => {
      const CHECKABLE_TYPES = new Set([
        'singleChoiceExercise', 'multipleChoiceExercise', 'anagramExercise',
        'boggleExercise', 'wordPoolExercise', 'dragLabelImageExercise',
        'dragLabelTextExercise', 'dragLabelBlankExercise', 'matchLinesExercise', 'labelImageExercise', 'oddOneOutExercise',
        'sortingExercise', 'unjumbleExercise', 'orderSentencesExercise', 'rankExercise', 'typeAnswerExercise', 'trueFalseExercise', 'abcdExercise', 'chooseWordExercise', 'fixMistakeExercise', 'fillGapsExercise', 'tableFillExercise',
        'missingLettersExercise', 'pronounceExercise',
      ]);
      return (classSlide?.elements || []).some((el) => CHECKABLE_TYPES.has(el.type));
    }, [classSlide]);

    // Helper to filter out isCorrect / correctLabelId from exercises for students
    const getStudentSafeElement = useCallback((element) => {
      if (userRole !== 'student') return element;
      if (!element.type?.includes('Exercise')) return element;

      const next = { ...element, content: { ...element.content } };
      if (Array.isArray(element.content?.options)) {
        next.content.options = element.content.options.map(({ isCorrect, ...opt }) => opt);
      }
      if (Array.isArray(element.content?.slots)) {
        next.content.slots = element.content.slots.map(({ correctLabelId, ...slot }) => slot);
      }
      if (Array.isArray(element.content?.correctAnswers)) {
        delete next.content.correctAnswers;
      }
      if (Array.isArray(element.content?.gaps)) {
        next.content.gaps = element.content.gaps.map(({ correctAnswers, ...g }) => g);
      }
      if (Array.isArray(element.content?.rows) && element.type === 'oddOneOutExercise') {
        next.content.rows = element.content.rows.map(({ oddId, explanation, ...row }) => row);
      }
      if (Array.isArray(element.content?.items) && element.type === 'sortingExercise') {
        next.content.items = element.content.items.map(({ categoryId, ...it }) => it);
      }
      if (Array.isArray(element.content?.items) && element.type === 'rankExercise') {
        // The item order in content.items IS the correct ranking. Shuffle before
        // sending to the student so React DevTools can't reveal the answer.
        // Grading uses the full (unsanitized) element in handleRankCheck.
        const shuffled = [...element.content.items];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        next.content.items = shuffled.map((it) => ({ id: it.id, text: it.text }));
      }
      if (Array.isArray(element.content?.statements) && element.type === 'trueFalseExercise') {
        next.content.statements = element.content.statements.map(({ correctAnswer, ...s }) => s);
      }
      if (Array.isArray(element.content?.questions) && element.type === 'abcdExercise') {
        next.content.questions = element.content.questions.map(({ correctOptionId, ...q }) => q);
      }
      if (Array.isArray(element.content?.sentences) && element.type === 'chooseWordExercise') {
        next.content.sentences = element.content.sentences.map(({ correctIndices, ...s }) => s);
      }
      if (Array.isArray(element.content?.sentences) && element.type === 'fixMistakeExercise') {
        // Strip the correction from each mistake token so the correct answer
        // never reaches the student's browser. The wrong word stays.
        next.content.sentences = element.content.sentences.map((s) => ({
          ...s,
          text: (s.text || '').replace(/\[([^\]]+)\]/g, (_, inner) => {
            const parts = inner.split('|').map((p) => p.trim()).filter(Boolean);
            const wrong = parts[0] || '';
            return `[${wrong}]`;
          }),
        }));
      }
      if (element.type === 'tableFillExercise') {
        next.content = sanitizeTableFillContent(element.content);
      }
      if (element.type === 'missingLettersExercise') {
        const word = element.content?.word || '';
        const indices = Array.isArray(element.content?.missingIndices) ? element.content.missingIndices : [];
        const set = new Set(indices.map((n) => Number(n)));
        // Replace hidden letters with a placeholder so the correct letter is
        // never sent to the student's browser. The element still knows the
        // slot layout because visible letters remain in their positions.
        const masked = word.split('').map((ch, idx) => (set.has(idx) ? ' ' : ch)).join('');
        next.content.word = masked;
      }
      return next;
    }, [userRole]);

    // Get effective interactive state (prefer synced, fall back to local)
    const getInteractiveState = useCallback((elementId, elementType) => {
      const syncedState = syncedInteractiveStates[elementId];

      // Check if we have recent local interaction - use local state
      const localTime = useClassroomStore.getState().localInteractionTimestamps[elementId] || 0;
      const isLocalRecent = localTime > 0 && (Date.now() - localTime < 1500);

      if (isLocalRecent) {
        // Use local state for immediate feedback
        switch (elementType) {
          case 'spinWheel':
            return localInteractiveUI.wheelRotations[elementId];
          case 'flipCard':
            return localInteractiveUI.flipStates[elementId];
          case 'dice':
            return localInteractiveUI.diceValues[elementId];
          case 'inputField':
            return localInteractiveUI.inputValues[elementId];
          case 'notebook':
            return localInteractiveUI.inputValues[elementId];
          case 'checkbox':
            return localInteractiveUI.checkboxStates[elementId];
          case 'like':
            return localInteractiveUI.likeStates[elementId];
          case 'rating':
            return localInteractiveUI.ratingStates[elementId];
          case 'shop':
            return localInteractiveUI.shopStates[elementId];
          case 'cardDeck':
            return localInteractiveUI.cardDeckStates[elementId];
          case 'book':
            return localInteractiveUI.bookStates[elementId];
          case 'highlight':
            return localInteractiveUI.highlightStates[elementId];
          case 'bingo':
            return localInteractiveUI.bingoStates[elementId];
          case 'kissMarryAvoid':
            return localInteractiveUI.kissMarryAvoidStates[elementId];
          default:
            return null;
        }
      }

      // Use synced state from server
      return syncedState?.state ?? null;
    }, [syncedInteractiveStates, localInteractiveUI]);

    // Debug logging for session ID issues
    useEffect(() => {
      if (currentLessonForClass) {
        console.log('[VirtualClassroom] Lesson data:', {
          id: currentLessonForClass.id,
          sourceType: currentLessonForClass.sourceType || currentLessonForClass.source_type,
          sourceId: currentLessonForClass.sourceId || currentLessonForClass.source_id,
          clubId: currentLessonForClass.clubId || currentLessonForClass.club_id,
          teacherId: currentLessonForClass.teacherId || currentLessonForClass.teacher_id,
          userRole,
          lessonSessionId
        });
      }
    }, [currentLessonForClass, userRole, lessonSessionId]);

    // Ensure the lesson's presentation is loaded with all slides. If
    // CameraPreviewModal already prefetched it, this is a no-op (dedup'd by
    // fullPresentationFetchedIds in educationStore).
    useEffect(() => {
      const presentationId = currentLessonForClass?.presentationId || syncedPresentationId;
      if (!presentationId) return;
      let cancelled = false;
      setPresentationLoadError(false);
      fetchPresentationWithSlides(presentationId).then((result) => {
        if (cancelled) return;
        if (!result) setPresentationLoadError(true);
      });
      return () => { cancelled = true; };
    }, [currentLessonForClass?.presentationId, syncedPresentationId, fetchPresentationWithSlides]);

    // Timer and presentation polling setup
    useEffect(() => {
      if (!lessonSessionId) {
        return;
      }

      startTimerPolling(lessonSessionId);
      startPresentationPolling(lessonSessionId);
      startVideoStatePolling(lessonSessionId);

      return () => {
        stopTimerPolling();
        stopPresentationPolling();
        stopVideoStatePolling();
      };
    }, [lessonSessionId, startTimerPolling, stopTimerPolling, startPresentationPolling, stopPresentationPolling, startVideoStatePolling, stopVideoStatePolling]);

    // Local timer countdown (updates every second for smooth display)
    useEffect(() => {
      if (timerActive) {
        timerIntervalRef.current = setInterval(() => {
          updateTimerLocally();
        }, 1000);
      } else {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }, [timerActive, updateTimerLocally]);

    // Auto-end watcher. Warns at scheduled_end + 5min, hard-ends at +10min.
    // Runs on both teacher and student; teacher's client also marks the DB row
    // completed via /api/schedule/complete (idempotent — 409 if already done).
    const autoEndFiredRef = useRef(false);
    useEffect(() => {
      const lessonId = currentLessonForClass?.id;
      if (!lessonId) return;
      autoEndFiredRef.current = false;

      const fireAutoEnd = async () => {
        if (autoEndFiredRef.current) return;
        autoEndFiredRef.current = true;
        if (userRole === 'teacher' && !String(lessonId).startsWith('club')) {
          try {
            await api.post(`/api/schedule/complete/${lessonId}`, {});
          } catch (err) {
            console.error('Auto-end: complete failed', err);
          }
        }
        const dashboardPath = userRole === 'teacher' ? '/dashboard/teacher' : '/dashboard/student';
        navigate(dashboardPath);
        onEndClass();
      };

      const tick = () => {
        const { shouldEnd } = updateAutoEndCountdown();
        if (shouldEnd) fireAutoEnd();
      };

      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }, [currentLessonForClass?.id, userRole, updateAutoEndCountdown, navigate, onEndClass]);

    // Play sound when timer ends
    useEffect(() => {
      if (timerJustEnded) {
        let audioContext = null;
        let oscillator = null;
        let isCancelled = false;
        const timeouts = [];

        // Create and play a gentle notification sound
        const playTimerSound = () => {
          try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;

            audioContext = new AudioContextClass();
            oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.value = 0.15;

            oscillator.start();

            // Three gentle beeps with cleanup
            timeouts.push(setTimeout(() => { if (!isCancelled) gainNode.gain.value = 0; }, 150));
            timeouts.push(setTimeout(() => { if (!isCancelled) gainNode.gain.value = 0.15; }, 300));
            timeouts.push(setTimeout(() => { if (!isCancelled) gainNode.gain.value = 0; }, 450));
            timeouts.push(setTimeout(() => { if (!isCancelled) gainNode.gain.value = 0.15; }, 600));
            timeouts.push(setTimeout(() => {
              if (!isCancelled && oscillator && audioContext) {
                gainNode.gain.value = 0;
                oscillator.stop();
                audioContext.close().catch(() => {});
              }
            }, 750));
          } catch (error) {
            console.error('Failed to play timer sound:', error);
          }
        };

        playTimerSound();

        // Auto-clear the "just ended" state after showing notification
        const clearStateTimeout = setTimeout(() => {
          clearTimerJustEnded();
        }, 3000);

        return () => {
          isCancelled = true;
          clearTimeout(clearStateTimeout);
          timeouts.forEach(t => clearTimeout(t));
          if (oscillator) {
            try { oscillator.stop(); } catch (e) { /* already stopped */ }
          }
          if (audioContext && audioContext.state !== 'closed') {
            audioContext.close().catch(() => {});
          }
        };
      }
    }, [timerJustEnded, clearTimerJustEnded]);

    // Setup chat conversation and polling
    useEffect(() => {
      const currentUserId = user?.id;

      // For club lessons, use group chat
      if (isClubLesson && lessonSessionId && currentUserId) {
        setChatAvailable(true);

        const setupClubChat = async () => {
          await fetchClubMessages(lessonSessionId);
          startClubPolling(lessonSessionId);
        };

        setupClubChat();

        return () => {
          stopClubPolling();
        };
      }

      // For individual lessons, use 1-to-1 chat
      const otherUserId = userRole === 'teacher'
        ? currentLessonForClass?.studentId
        : currentLessonForClass?.teacherId;

      if (!currentUserId || !otherUserId) {
        setChatAvailable(false);
        return;
      }
      setChatAvailable(true);

      const setupChat = async () => {
        const result = await startConversation(currentUserId, otherUserId);
        if (result.conversationId) {
          setConversationId(result.conversationId);
          await fetchMessages(result.conversationId, currentUserId);
          startPolling(result.conversationId, currentUserId);
        }
      };

      setupChat();

      return () => {
        stopPolling();
      };
    }, [user?.id, userRole, currentLessonForClass?.studentId, currentLessonForClass?.teacherId, startConversation, fetchMessages, startPolling, stopPolling, isClubLesson, lessonSessionId, fetchClubMessages, startClubPolling, stopClubPolling]);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, clubMessages]);

    // Start presence tracking when entering classroom
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;

      // Current participant info for presence tracking
      const currentParticipant = {
        id: user.id,
        name: user.name || user.firstName || (userRole === 'teacher' ? tt('roleTeacher') : tt('roleStudent')),
        avatarUrl: user.avatarUrl || user.avatar_url,
        role: userRole
      };

      startPresencePolling(lessonSessionId, currentParticipant);

      // Fire 'left' if the user closes the tab / navigates away without hitting
      // the "End lesson" button. sendBeacon survives page unload — a normal
      // fetch(DELETE) gets cancelled. Uses POST with a body flag the presence
      // endpoint interprets as a leave.
      const handleUnload = () => {
        try {
          const beaconUrl = `${API.PRESENTATION_STATE}/${lessonSessionId}/presence/${user.id}/leave`;
          if (navigator.sendBeacon) {
            navigator.sendBeacon(beaconUrl);
          } else {
            fetch(`${API.PRESENTATION_STATE}/${lessonSessionId}/presence/${user.id}`, {
              method: 'DELETE',
              keepalive: true
            });
          }
        } catch (e) { /* ignore */ }
      };
      window.addEventListener('pagehide', handleUnload);
      window.addEventListener('beforeunload', handleUnload);

      return () => {
        window.removeEventListener('pagehide', handleUnload);
        window.removeEventListener('beforeunload', handleUnload);
        stopPresencePolling(lessonSessionId, user.id);
      };
    }, [lessonSessionId, user?.id, user?.name, user?.firstName, user?.avatarUrl, user?.avatar_url, userRole, startPresencePolling, stopPresencePolling]);

    // Connect to LiveKit video room when entering classroom
    useEffect(() => {
      if (!currentLessonForClass || !user?.id) {
        return;
      }

      // Fetch LiveKit token (uses httpOnly cookies for auth)
      const connectToVideo = async () => {
        try {
          await fetchVideoToken(currentLessonForClass);
        } catch (error) {
          console.error('Failed to connect to video room:', error);
        }
      };

      connectToVideo();

      // Cleanup on unmount
      return () => {
        disconnectVideo();
      };
    }, [currentLessonForClass?.id, user?.id, fetchVideoToken, disconnectVideo]);

    // Reset video state when leaving classroom
    useEffect(() => {
      return () => {
        resetVideo();
      };
    }, [resetVideo]);

    // Student answers sync - send answers to server when student types
    useEffect(() => {
      if (!lessonSessionId || !user?.id || userRole === 'teacher') return;

      // Debounce sending answers to avoid too many requests
      const timeoutId = setTimeout(() => {
        if (Object.keys(studentInputValues).length > 0) {
          const currentParticipant = {
            id: user.id,
            name: user.name || user.firstName || tt('studentDefault')
          };
          sendStudentAnswers(lessonSessionId, currentParticipant, studentInputValues, classSlideIndex);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }, [studentInputValues, lessonSessionId, user?.id, user?.name, user?.firstName, userRole, classSlideIndex, sendStudentAnswers]);

    // Teacher polls for student answers
    useEffect(() => {
      if (!lessonSessionId || userRole !== 'teacher') return;

      startAnswersPolling(lessonSessionId, classSlideIndex);

      return () => {
        stopAnswersPolling();
      };
    }, [lessonSessionId, userRole, classSlideIndex, startAnswersPolling, stopAnswersPolling]);

    // Teacher polls for student exercise answers (quiz selections and correctness)
    useEffect(() => {
      if (!lessonSessionId || userRole !== 'teacher') return;

      startExerciseAnswersPolling(lessonSessionId, classSlideIndex);

      return () => {
        stopExerciseAnswersPolling();
      };
    }, [lessonSessionId, userRole, classSlideIndex, startExerciseAnswersPolling, stopExerciseAnswersPolling]);

    // Teacher answers sync - send answers to server when teacher types in editable fields
    useEffect(() => {
      if (!lessonSessionId || !user?.id || userRole !== 'teacher') return;

      // Debounce sending answers to avoid too many requests
      const timeoutId = setTimeout(() => {
        if (Object.keys(studentInputValues).length > 0) {
          sendTeacherAnswers(lessonSessionId, studentInputValues, classSlideIndex);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }, [studentInputValues, lessonSessionId, user?.id, userRole, classSlideIndex, sendTeacherAnswers]);

    // Students poll for teacher answers
    useEffect(() => {
      if (!lessonSessionId || userRole !== 'student') return;

      startTeacherAnswersPolling(lessonSessionId, syncedSlideIndex);

      return () => {
        stopTeacherAnswersPolling();
      };
    }, [lessonSessionId, userRole, syncedSlideIndex, startTeacherAnswersPolling, stopTeacherAnswersPolling]);

    // Element positions sync - poll for positions (both teacher and student)
    // Students use synced slide index, teachers use their local slide index
    const slideIndexForPositions = userRole === 'student' ? syncedSlideIndex : classSlideIndex;
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;

      startPositionsPolling(lessonSessionId, slideIndexForPositions, user.id);

      return () => {
        stopPositionsPolling();
      };
    }, [lessonSessionId, slideIndexForPositions, user?.id, startPositionsPolling, stopPositionsPolling]);

    // Send element positions when they change
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;

      // Debounce sending positions
      const timeoutId = setTimeout(() => {
        if (Object.keys(studentElementPositions).length > 0) {
          sendElementPositions(lessonSessionId, studentElementPositions, slideIndexForPositions, user.id);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [studentElementPositions, lessonSessionId, user?.id, slideIndexForPositions, sendElementPositions]);

    // Interactive elements state sync - poll for states (both teacher and student)
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;

      startInteractivePolling(lessonSessionId, slideIndexForPositions, user.id);

      return () => {
        stopInteractivePolling();
      };
    }, [lessonSessionId, slideIndexForPositions, user?.id, startInteractivePolling, stopInteractivePolling]);

    // Drawing strokes sync - poll for strokes (both teacher and student)
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;

      // Only start polling when presentation is shared (or for teacher)
      if (userRole === 'student' && !effectivePresentationShared) return;

      startDrawingPolling(lessonSessionId, slideIndexForPositions);

      return () => {
        stopDrawingPolling();
      };
    }, [lessonSessionId, slideIndexForPositions, user?.id, userRole, effectivePresentationShared, startDrawingPolling, stopDrawingPolling]);

    // Teacher pointer sync - students poll to mirror the teacher's cursor.
    // Teacher does not poll itself (it owns the source state).
    useEffect(() => {
      if (!lessonSessionId || !user?.id) return;
      if (userRole !== 'student') return;
      if (!effectivePresentationShared) return;

      startPointerStatePolling(lessonSessionId);
      return () => {
        stopPointerStatePolling();
      };
    }, [lessonSessionId, user?.id, userRole, effectivePresentationShared, startPointerStatePolling, stopPointerStatePolling]);

    // Throttled teacher pointer broadcast (~80ms). The trailing-edge timer
    // ensures the final position is delivered after the mouse stops moving.
    const broadcastPointerState = useCallback((payload) => {
      if (!lessonSessionId || userRole !== 'teacher') return;
      const ref = pointerBroadcastRef.current;
      ref.lastPayload = payload;
      const now = Date.now();
      const elapsed = now - ref.lastSentAt;
      const minInterval = 80;
      const flush = () => {
        ref.lastSentAt = Date.now();
        ref.scheduled = null;
        sendPointerState(lessonSessionId, ref.lastPayload);
      };
      if (elapsed >= minInterval) {
        if (ref.scheduled) { clearTimeout(ref.scheduled); ref.scheduled = null; }
        flush();
      } else if (!ref.scheduled) {
        ref.scheduled = setTimeout(flush, minInterval - elapsed);
      }
    }, [lessonSessionId, userRole, sendPointerState]);

    // Broadcast on/off transitions for the teacher pointer so students stop
    // (or start) seeing the dot immediately, without waiting for a mouse move.
    useEffect(() => {
      if (userRole !== 'teacher' || !lessonSessionId) return;
      if (!pointerActive) {
        broadcastPointerState({
          active: false,
          x: null,
          y: null,
          slideIndex: slideIndexForPositions,
          teacherId: user?.id
        });
      }
    }, [pointerActive, userRole, lessonSessionId, slideIndexForPositions, user?.id, broadcastPointerState]);

    // Drawing handlers
    const handleStrokeComplete = useCallback((stroke) => {
      // Add to local state immediately for responsiveness
      addLocalStroke(stroke);

      // Send to server for sync
      if (lessonSessionId) {
        sendDrawingStroke(lessonSessionId, stroke, slideIndexForPositions, user?.id);
      }
    }, [lessonSessionId, slideIndexForPositions, user?.id, addLocalStroke, sendDrawingStroke]);

    const handleEraseStroke = useCallback((strokeIds) => {
      if (lessonSessionId) {
        eraseStrokes(lessonSessionId, strokeIds, slideIndexForPositions);
      }
    }, [lessonSessionId, slideIndexForPositions, eraseStrokes]);

    const handleClearAllDrawings = useCallback(() => {
      if (lessonSessionId) {
        clearAllStrokes(lessonSessionId, slideIndexForPositions);
      }
    }, [lessonSessionId, slideIndexForPositions, clearAllStrokes]);

    const toggleDrawingTool = useCallback((tool) => {
      // If clicking on same tool, deactivate it
      if (drawingTool === tool) {
        setDrawingTool(null);
      } else {
        setDrawingTool(tool);
        // Deactivate other tools when drawing is active
        setLaserActive(false);
        setHighlighterActive(false);
        setPointerActive(false);
        setMagnifierActive(false);
      }
    }, [drawingTool, setDrawingTool, setLaserActive, setHighlighterActive, setPointerActive, setMagnifierActive]);

    // Build participants list for display - uses online presence tracking
    const classroomParticipants = useMemo(() => {
      // Use participants from presence tracking (actual online users)
      // The participants array is now populated by presence polling
      if (participants.length > 0) {
        // Sort: teachers first, then students
        return [...participants].sort((a, b) => {
          if (a.role === 'teacher' && b.role !== 'teacher') return -1;
          if (a.role !== 'teacher' && b.role === 'teacher') return 1;
          return 0;
        });
      }

      // Fallback for when presence tracking hasn't populated yet
      // Show current user as the only participant
      const list = [];
      if (user?.id) {
        list.push({
          id: user.id,
          name: user.name || user.firstName || (userRole === 'teacher' ? tt('roleTeacher') : tt('roleStudent')),
          role: userRole,
          avatarUrl: user.avatarUrl || user.avatar_url
        });
      }

      return list;
    }, [participants, user, userRole]);

    // Handle sending individual chat message (callback for ClassroomChat)
    const handleSendIndividualMessage = async (text) => {
      const currentUserId = user?.id;
      const otherUserId = userRole === 'teacher'
        ? currentLessonForClass?.studentId
        : currentLessonForClass?.teacherId;

      if (!currentUserId || !otherUserId) {
        showAlert(tt('errorTitle'), tt('errRecipient'), 'error');
        throw new Error('Missing user IDs');
      }

      const result = await sendMessage(currentUserId, otherUserId, text);

      if (result.success && result.conversationId && !conversationId) {
        setConversationId(result.conversationId);
        startPolling(result.conversationId, currentUserId);
      } else if (!result.success) {
        showAlert(tt('errorTitle'), tt('errSendMessage'), 'error');
        throw new Error('Send failed');
      }
    };

    // Handle sending club chat message (callback for ClassroomChat)
    const handleSendClubChatMessage = async (text) => {
      const currentUserId = user?.id;
      const senderName = user?.name || user?.firstName || (userRole === 'teacher' ? tt('roleTeacher') : tt('roleStudent'));
      const senderAvatarUrl = user?.avatarUrl || user?.avatar_url;

      const result = await sendClubMessage(lessonSessionId, clubId, currentUserId, senderName, senderAvatarUrl, text);
      if (!result?.success) {
        showAlert(tt('errorTitle'), tt('errSendMessage'), 'error');
        throw new Error('Send failed');
      }
    };

    const handleEndLesson = async () => {
      if (userRole === 'teacher') {
        setShowTeacherReport(true);
        return;
      }

      const lessonId = currentLessonForClass?.id;
      if (!lessonId || String(lessonId).startsWith('club')) {
        setShowRatingModal(true);
        return;
      }

      // Student ends the lesson themselves: claim credits the balance and
      // returns the awarded stars in one call. Idempotent on the server — if
      // the teacher has already completed the lesson, the persisted value is
      // returned without re-crediting.
      setEndingLesson(true);
      try {
        const res = await fetch(`/api/schedule/lesson/${lessonId}/claim-my-stars`, {
          credentials: 'include',
          method: 'POST',
        });
        if (res.ok) {
          const data = await res.json();
          setStarsResult({
            stars: data.stars ?? 0,
            percentage: data.percentage ?? 0,
            earnedPoints: data.earnedPoints ?? 0,
            totalPoints: data.totalPoints ?? 0,
          });
          setEndingLesson(false);
          setShowStarsResult(true);
          return;
        }
      } catch (_) {
        // Fall through to rating modal on network error.
      }
      setEndingLesson(false);
      setShowRatingModal(true);
    };

    const handleSubmitRating = async (ratingData) => {
      // Submit review to backend
      try {
        const reviewData = {
          studentId: user?.id || null,
          studentName: user?.name || user?.firstName || tt('roleStudent'),
          teacherId: currentLessonForClass?.teacherId || null,
          teacherName: currentLessonForClass?.teacherName || tt('roleTeacher'),
          lessonId: currentLessonForClass?.id || null,
          lessonTopic: currentLessonForClass?.topic || currentLessonForClass?.title || currentLessonForClass?.programLessonTitle || tt('lessonDefault'),
          rating: ratingData.rating,
          comment: ratingData.comment || null
        };

        // Use relative URL - proxy will forward to backend
        const response = await fetch('/api/reviews', {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reviewData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Review submission failed:', errorData);
        }
      } catch (error) {
        console.error('❌ Failed to submit review:', error);
      }

      setShowRatingModal(false);
      // Navigate to the appropriate dashboard before ending class
      const dashboardPath = userRole === 'teacher' ? '/dashboard/teacher' : '/dashboard/student';
      navigate(dashboardPath);
      onEndClass();
    };

    const handleSkipRating = () => {
      setShowRatingModal(false);
      const dashboardPath = userRole === 'teacher' ? '/dashboard/teacher' : '/dashboard/student';
      navigate(dashboardPath);
      onEndClass();
    };

    const handleSubmitTeacherReport = async (reportData) => {
      // Update student progress if this was a program lesson
      if (currentLessonForClass?.programId) {
        onSetStudentPrograms(studentPrograms.map(sp => {
          if (sp.studentId === currentLessonForClass.studentId &&
              sp.programId === currentLessonForClass.programId) {
            const program = programs.find(p => p.id === sp.programId);
            const newIndex = Math.min(sp.currentLessonIndex + 1, (program?.lessons.length || 1) - 1);
            return {
              ...sp,
              currentLessonIndex: newIndex,
              completedLessons: [...sp.completedLessons, currentLessonForClass.programLessonId],
              status: newIndex >= (program?.lessons.length || 1) - 1 ? 'completed' : 'active'
            };
          }
          return sp;
        }));
      }

      // Submit report to backend
      try {
        const reportPayload = {
          teacherId: user?.id || null,
          teacherName: user?.name || user?.firstName || tt('roleTeacher'),
          studentId: currentLessonForClass?.studentId || null,
          studentName: currentLessonForClass?.studentName || tt('roleStudent'),
          lessonId: currentLessonForClass?.id || null,
          lessonTopic: currentLessonForClass?.topic || currentLessonForClass?.title || currentLessonForClass?.programLessonTitle || tt('lessonDefault'),
          lessonType: currentLessonForClass?.sourceType === 'club' ? 'group' : 'individual',
          duration: currentLessonForClass?.duration || 60,
          presentationErrors: reportData?.presentationErrors || false,
          errorDetails: reportData?.errorDetails || null,
          studentLiked: reportData?.studentLiked,
          studentEngagement: reportData?.studentEngagement || null,
          technicalIssues: reportData?.technicalIssues || false,
          technicalDetails: reportData?.technicalDetails || null,
          notes: reportData?.notes || null
        };

        // api client auto-refreshes the access token on 401 so long-running
        // lessons (>15 min) don't fail the report submit with NO_TOKEN.
        const { error, status } = await api.post('/api/teacher-reports', reportPayload);

        if (error) {
          if (status === 409) {
            console.log('Report already exists for this lesson, skipping completion');
            setShowTeacherReport(false);
            navigate('/dashboard/teacher');
            onEndClass();
            return;
          }
          console.error('Teacher report submission failed:', status, error);
          toast.error(error || tt('errSaveReport'));
          return;
        }
      } catch (error) {
        console.error('Failed to submit teacher report:', error);
        toast.error(tt('errSaveReport'));
        return;
      }

      // Mark lesson as completed and trigger homework auto-assignment
      if (currentLessonForClass?.id && !String(currentLessonForClass.id).startsWith('club')) {
        try {
          console.log('Completing lesson with ID:', currentLessonForClass.id);
          const { data: completeData, error: completeError, status: completeStatus } = await api.post(
            `/api/schedule/complete/${currentLessonForClass.id}`,
            {}
          );

          if (completeError) {
            if (completeError === 'Lesson already completed') {
              console.log('Lesson was already completed');
            } else if (completeStatus === 400 && typeof completeError === 'string' && completeError.startsWith('Учень ще має час під')) {
              toast.error(completeError);
              return;
            } else {
              console.error('Failed to complete lesson - API error:', completeError);
            }
          } else {
            console.log('Lesson completed successfully');
            if (completeData?.homeworkAssigned) {
              console.log('Homework auto-assigned:', completeData.homeworkAssigned);
            }
          }
        } catch (error) {
          console.error('Failed to complete lesson:', error);
        }
      }

      setShowTeacherReport(false);
      // Navigate to the appropriate dashboard before ending class
      navigate('/dashboard/teacher');
      onEndClass();
    };

    const handlePrevSlide = () => {
      if (classSlideIndex > 0) {
        const newIndex = classSlideIndex - 1;
        setClassSlideIndex(newIndex);
        // Sync slide index and presentationId to server for students
        if (lessonSessionId) {
          updatePresentationState(lessonSessionId, { slideIndex: newIndex, presentationId: teacherPresentation?.id });
        }
      }
    };

    const handleNextSlide = () => {
      if (teacherPresentation && classSlideIndex < teacherPresentation.slides.length - 1) {
        const newIndex = classSlideIndex + 1;
        setClassSlideIndex(newIndex);
        // Sync slide index and presentationId to server for students
        if (lessonSessionId) {
          updatePresentationState(lessonSessionId, { slideIndex: newIndex, presentationId: teacherPresentation?.id });
        }
      }
    };

    const handleTogglePresentationShare = () => {
      const newShared = !presentationShared;
      setPresentationShared(newShared);
      // Sync to server so students can see - include presentationId so students show the same presentation
      if (lessonSessionId) {
        updatePresentationState(lessonSessionId, {
          shared: newShared,
          slideIndex: classSlideIndex,
          teacherId: user?.id,
          presentationId: teacherPresentation?.id || null
        });
      }
    };

    // Timer helpers
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleStartTimer = async (minutes) => {
      if (!lessonSessionId) return;

      await startTimer(lessonSessionId, minutes * 60, user?.id);
      setShowTimerModal(false);
    };

    const handleStopTimer = async () => {
      if (!lessonSessionId) return;

      await stopTimer(lessonSessionId);
    };

    // Handle student drag for movable elements - optimized with requestAnimationFrame.
    // Accepts a batch of updates so grouped elements can be moved together in a single
    // frame. Each entry is { id, position: { x, y } }.
    const handleStudentDrag = useCallback((updates) => {
      if (!updates || updates.length === 0) return;

      // Store the pending updates (last-write-wins per frame)
      pendingPositionRef.current = { updates };

      // Use requestAnimationFrame to batch position updates
      if (!dragRafRef.current) {
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null;
          if (pendingPositionRef.current) {
            const { updates: pending } = pendingPositionRef.current;
            setStudentElementPositions(prev => {
              const next = { ...prev };
              for (const u of pending) next[u.id] = u.position;
              return next;
            });
          }
        });
      }
    }, [setStudentElementPositions]);

    // Extract clientX/clientY from either a mouse or touch event.
    const getEventPoint = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      }
      if (e.changedTouches && e.changedTouches.length > 0) {
        return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
      }
      return { clientX: e.clientX, clientY: e.clientY };
    };

    // Handle drag start - cache rect and set dragging state.
    // Uses raw mouse/touch input so mobile works (HTML5 DnD is mouse-only).
    const handleDragStart = useCallback((e, elementId, canDrag) => {
      if (!canDrag) return;

      // Stop the browser from scrolling / synthesizing mouse events on touch.
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();

      // Cache the canvas rect at drag start to avoid layout thrashing
      if (boardRef.current) {
        cachedRectRef.current = boardRef.current.getBoundingClientRect();
      }

      // Snapshot the drag unit — every element sharing this element's groupId
      // moves as one, so grouped covers/labels stay locked to their partner.
      const rect = cachedRectRef.current;
      const slideElements = classSlide?.elements || [];
      const primary = slideElements.find(el => el.id === elementId);
      // The whole group moves as one unit. Legacy data can have `canStudentMove`
      // set on only one member of a group (e.g. the shell but not its cover);
      // filtering here would leave the cover behind and defeat the reveal.
      const dragUnit = primary?.groupId
        ? slideElements.filter(el => el.groupId === primary.groupId)
        : (primary ? [primary] : []);

      const resolveStartPos = (el) => {
        const localPos = studentElementPositions[el.id];
        const syncedPos = syncedElementPositions[el.id];
        if (localPos) return { x: localPos.x, y: localPos.y };
        if (syncedPos && syncedPos.x !== undefined) return { x: syncedPos.x, y: syncedPos.y };
        return { x: el.position.x, y: el.position.y };
      };

      const { clientX, clientY } = getEventPoint(e);
      const cursorStartX = rect ? ((clientX - rect.left) / rect.width) * 100 : 0;
      const cursorStartY = rect ? ((clientY - rect.top) / rect.height) * 100 : 0;

      dragStartRef.current = {
        cursorX: cursorStartX,
        cursorY: cursorStartY,
        members: dragUnit.map(el => {
          const start = resolveStartPos(el);
          return { id: el.id, startX: start.x, startY: start.y };
        })
      };

      setDraggingElementId(elementId);
      for (const m of dragStartRef.current.members) {
        trackLocalDrag(m.id);
      }

      // Clear any pending timeout
      if (clearDragTimeoutRef.current) {
        clearTimeout(clearDragTimeoutRef.current);
        clearDragTimeoutRef.current = null;
      }
    }, [trackLocalDrag, classSlide, studentElementPositions, syncedElementPositions]);

    // Handle drag move - use cached rect. Works for both mouse and touch.
    // Applies the cursor delta since drag-start to every snapshotted group member,
    // clamped to the board, so grouped elements move together.
    const handleDragMove = useCallback((e, elementId, canDrag) => {
      if (!canDrag) return;
      const start = dragStartRef.current;
      if (!start || start.members.length === 0) return;

      const { clientX, clientY } = getEventPoint(e);
      if (clientX == null || clientY == null) return;

      // Use cached rect, or get new one if not available
      let rect = cachedRectRef.current;
      if (!rect && boardRef.current) {
        rect = boardRef.current.getBoundingClientRect();
        cachedRectRef.current = rect;
      }

      if (!rect) return;

      const cursorX = ((clientX - rect.left) / rect.width) * 100;
      const cursorY = ((clientY - rect.top) / rect.height) * 100;
      const deltaX = cursorX - start.cursorX;
      const deltaY = cursorY - start.cursorY;

      const updates = start.members.map(m => ({
        id: m.id,
        position: {
          x: Math.max(0, Math.min(100, m.startX + deltaX)),
          y: Math.max(0, Math.min(100, m.startY + deltaY))
        }
      }));
      handleStudentDrag(updates);
    }, [handleStudentDrag]);

    // Handle drag end - cleanup and schedule timestamp clear
    const handleDragEnd = useCallback(() => {
      // Cancel any pending RAF
      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }

      // Apply any pending position(s) immediately for every group member.
      // Capture the final positions so the trash hit-test below sees the
      // actual drop point, not the stale state from the last RAF tick.
      const finalPositions = {};
      if (pendingPositionRef.current) {
        const { updates } = pendingPositionRef.current;
        if (updates && updates.length > 0) {
          for (const u of updates) finalPositions[u.id] = u.position;
          setStudentElementPositions(prev => {
            const next = { ...prev };
            for (const u of updates) next[u.id] = u.position;
            return next;
          });
        }
        pendingPositionRef.current = null;
      }

      // Capture the drag unit for the deferred timestamp clear before we drop it
      const memberIds = (dragStartRef.current?.members || []).map(m => m.id);

      // macOS-trash semantics — if any dragged member's final center lands
      // inside a smittnik on the current slide, hide the whole drag unit.
      const bins = (classSlide?.elements || []).filter(el => el.type === 'smittnik');
      if (memberIds.length > 0 && bins.length > 0) {
        const resolvePos = (id) => {
          if (finalPositions[id]) return finalPositions[id];
          if (studentElementPositions[id]) return studentElementPositions[id];
          const synced = syncedElementPositions[id];
          if (synced && synced.x !== undefined) return { x: synced.x, y: synced.y };
          const el = (classSlide?.elements || []).find(e => e.id === id);
          return el?.position || null;
        };
        const inBin = (pt) => bins.some(bin => {
          const bx = bin.position?.x ?? 50;
          const by = bin.position?.y ?? 50;
          const bw = bin.size?.width ?? 0;
          const bh = bin.size?.height ?? 0;
          return pt.x >= bx - bw / 2 && pt.x <= bx + bw / 2
            && pt.y >= by - bh / 2 && pt.y <= by + bh / 2;
        });
        const anyInBin = memberIds.some(id => {
          const p = resolvePos(id);
          return p && inBin(p);
        });
        if (anyInBin) {
          const slideKey = `${slideIndexForPositions}`;
          setTrashedElements(prev => {
            const existing = prev[slideKey] || new Set();
            const next = new Set(existing);
            for (const id of memberIds) next.add(id);
            return { ...prev, [slideKey]: next };
          });
        }
      }

      dragStartRef.current = null;

      // Clear cached rect
      cachedRectRef.current = null;

      // Clear dragging state
      setDraggingElementId(null);

      // Clear the local drag timestamp after a delay to allow server updates
      // Only set one timeout, clear any existing one
      if (clearDragTimeoutRef.current) {
        clearTimeout(clearDragTimeoutRef.current);
      }
      clearDragTimeoutRef.current = setTimeout(() => {
        for (const id of memberIds) clearLocalDragTimestamp(id);
        clearDragTimeoutRef.current = null;
      }, 1000);
    }, [clearLocalDragTimestamp, setStudentElementPositions, classSlide, studentElementPositions, syncedElementPositions, slideIndexForPositions]);

    // Cleanup RAF on unmount
    useLayoutEffect(() => {
      return () => {
        if (dragRafRef.current) {
          cancelAnimationFrame(dragRafRef.current);
        }
        if (clearDragTimeoutRef.current) {
          clearTimeout(clearDragTimeoutRef.current);
        }
      };
    }, []);

    // Global mouse + touch listeners while dragging. touchmove is passive:false
    // so preventDefault() stops the page from scrolling under the finger.
    useEffect(() => {
      if (!draggingElementId) return undefined;
      const activeId = draggingElementId;
      const onMouseMove = (e) => handleDragMove(e, activeId, true);
      const onTouchMove = (e) => {
        if (e.cancelable) e.preventDefault();
        handleDragMove(e, activeId, true);
      };
      const onEnd = () => handleDragEnd();
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onEnd);
      window.addEventListener('touchcancel', onEnd);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onEnd);
        window.removeEventListener('touchcancel', onEnd);
      };
    }, [draggingElementId, handleDragMove, handleDragEnd]);

    // Video room content wrapper
    const classroomContent = (
      <>
      <RotateDeviceOverlay />
      <div className="h-screen bg-white flex overflow-hidden">
      {/* Left side - Presentation area.
          Mobile: slide fills the viewport; video PiP, toolbar rail and chat drawer float
          on top as absolute overlays (see their own blocks).
          md+: flex column — video top, presentation middle, toolbar bottom, chat sits to
          the right as a sibling. */}
      <div className={`flex-1 overflow-hidden relative md:p-4 md:flex md:flex-col md:gap-0 ${chatOpen ? 'flex flex-col gap-0 p-2' : ''}`}>
        {/* Top - Video Gallery.
            Mobile: floats as PiP overlay in the top-right corner (above the toolbar rail),
            so slide has clear space on the left.
            Mobile + chat open: reflows to a horizontal strip at the top so the slide has
            room to breathe next to the chat panel.
            Desktop: normal flow, top-centered. */}
        <div className={`absolute top-2 right-2 z-20 flex md:static md:z-auto md:top-auto md:right-auto md:justify-center md:mb-4 ${chatOpen ? 'static z-auto top-auto right-auto justify-center mb-2' : ''}`}>
          {/* Collapsed pill on mobile — just a small expand button so slide is unobscured.
              Suppressed when chat is open since we want the full horizontal video strip visible. */}
          {mobileVideoCollapsed && !chatOpen ? (
            <button
              onClick={() => setMobileVideoCollapsed(false)}
              className="md:hidden inline-flex items-center gap-1 rounded-full px-2 py-1 shadow-xl text-white text-xs"
              style={{ backgroundColor: '#2A4D25' }}
              aria-label={tt('showVideoAria')}
            >
              <Video size={14} />
              {mobileVideoCount > 0 && <span>{mobileVideoCount}</span>}
            </button>
          ) : null}
          <div className={`${mobileVideoCollapsed && !chatOpen ? 'hidden md:flex' : 'flex'} flex-col md:flex-row md:items-center gap-1.5 md:gap-2 rounded-2xl px-1.5 py-1.5 md:px-3 md:py-2 shadow-xl ${chatOpen ? 'flex-row items-center gap-2 px-3 py-2' : ''}`} style={{ backgroundColor: '#2A4D25' }}>
            {/* Mobile collapse toggle — sits above participant tile so student can hide PiP with one tap.
                Hidden when chat is open (video strip stays in flow). */}
            {!chatOpen && (
              <button
                onClick={() => setMobileVideoCollapsed(true)}
                className="md:hidden self-end text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-lg transition flex-shrink-0"
                aria-label={tt('collapseVideoAria')}
              >
                <X size={14} />
              </button>
            )}

            {/* Horizontal scroll — left arrow. Visible on md+ or when chat is open on mobile. */}
            <button
              onClick={() => {
                const container = document.getElementById('users-scroll-container');
                if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
              }}
              className={`hidden md:inline-flex text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition flex-shrink-0 ${chatOpen ? '!inline-flex' : ''}`}
            >
              <ChevronLeft size={20} />
            </button>

            {/* Mobile single-view — previous participant (up chevron). Hidden when chat is open. */}
            {mobileVideoCount > 1 && !chatOpen && (
              <button
                onClick={() => setMobileVideoIndex((i) => (i - 1 + mobileVideoCount) % mobileVideoCount)}
                className="md:hidden self-center text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-lg transition flex-shrink-0"
                aria-label={tt('prevParticipantAria')}
              >
                <ChevronUp size={18} />
              </button>
            )}

            {/* Users container - Video Gallery */}
            <div
              id="users-scroll-container"
              className={`flex gap-2 scrollbar-hide md:flex-row md:overflow-x-auto md:overflow-y-visible md:max-w-[50vw] ${
                chatOpen ? 'flex-row overflow-x-auto max-w-[50vw]' : 'flex-col'
              }`}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* Show participants - SmartVideoGallery handles fallback */}
              <SmartVideoGallery
                classroomParticipants={classroomParticipants}
                mobileActiveIndex={mobileVideoIndex}
                onCountChange={setMobileVideoCount}
              />
            </div>

            {/* Mobile single-view — next participant (down chevron). Hidden when chat is open. */}
            {mobileVideoCount > 1 && !chatOpen && (
              <button
                onClick={() => setMobileVideoIndex((i) => (i + 1) % mobileVideoCount)}
                className="md:hidden self-center text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-lg transition flex-shrink-0"
                aria-label={tt('nextParticipantAria')}
              >
                <ChevronDown size={18} />
              </button>
            )}

            {/* Horizontal scroll — right arrow. Visible on md+ or when chat is open on mobile. */}
            <button
              onClick={() => {
                const container = document.getElementById('users-scroll-container');
                if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
              }}
              className={`hidden md:inline-flex text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition flex-shrink-0 ${chatOpen ? '!inline-flex' : ''}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Presentation Area — on mobile reserves the right edge for the video/toolbar rail
            so the entire 16:9 slide fits without being obscured. Takes remaining flex space on desktop
            (and when chat is open on mobile, since layout switches to column). */}
        <div className={`absolute inset-y-0 left-0 right-16 flex items-center justify-center overflow-hidden md:relative md:inset-auto md:flex-1 ${chatOpen ? 'relative inset-auto right-auto flex-1' : ''}`}>

          {/* Interactive Board - 16:9, fit-contain inside parent so the whole slide is visible. */}
          <div
            ref={boardRef}
            className={`bg-white rounded-2xl shadow-sm relative classroom-slide-scale overflow-hidden ${
              userRole === 'student' && !effectivePresentationShared ? 'border-2 border-brand-sage/50' : ''
            }`}
            style={{
              width: boardSize.width || undefined,
              height: boardSize.height || undefined,
              aspectRatio: '16 / 9',
              background: (userRole === 'teacher' || effectivePresentationShared)
                ? (classSlide?.backgroundColor?.includes('gradient')
                    ? classSlide.backgroundColor
                    : (classSlide?.backgroundColor || '#ffffff'))
                : '#E8F0E6',
              cursor: pointerActive ? 'none' : (laserActive ? 'crosshair' : (magnifierActive ? 'none' : 'default')),
              touchAction: magnifierActive ? 'none' : undefined,
              '--slide-scale': boardScale
            }}
            onMouseMove={(e) => {
              if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                if (magnifierActive) {
                  if (magnifierTouchModeRef.current) {
                    magnifierTouchModeRef.current = false;
                    setMagnifierTouchMode(false);
                  }
                  scheduleMagnifierUpdate({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                  });
                }
                if (pointerActive && userRole === 'teacher') {
                  const px = e.clientX - rect.left;
                  const py = e.clientY - rect.top;
                  setPointerPosition({ x: px, y: py });
                  // Broadcast as percentage so students render at the same relative location
                  if (rect.width > 0 && rect.height > 0) {
                    broadcastPointerState({
                      active: true,
                      x: (px / rect.width) * 100,
                      y: (py / rect.height) * 100,
                      slideIndex: slideIndexForPositions,
                      teacherId: user?.id
                    });
                  }
                }
              }
            }}
            onMouseLeave={() => {
              if (magnifierActive) {
                setMagnifierPosition({ x: -1, y: -1 });
              }
              if (pointerActive) {
                setPointerPosition({ x: -100, y: -100 });
                if (userRole === 'teacher') {
                  broadcastPointerState({
                    active: false,
                    x: null,
                    y: null,
                    slideIndex: slideIndexForPositions,
                    teacherId: user?.id
                  });
                }
              }
            }}
            onTouchStart={(e) => {
              if (!magnifierActive || !boardRef.current) return;
              const t = e.touches[0];
              if (!t) return;
              if (!magnifierTouchModeRef.current) {
                magnifierTouchModeRef.current = true;
                setMagnifierTouchMode(true);
              }
              const rect = boardRef.current.getBoundingClientRect();
              scheduleMagnifierUpdate({
                x: t.clientX - rect.left,
                y: t.clientY - rect.top
              });
            }}
            onTouchMove={(e) => {
              if (!magnifierActive || !boardRef.current) return;
              const t = e.touches[0];
              if (!t) return;
              const rect = boardRef.current.getBoundingClientRect();
              scheduleMagnifierUpdate({
                x: t.clientX - rect.left,
                y: t.clientY - rect.top
              });
            }}
            onTouchEnd={() => {
              if (magnifierActive) {
                setMagnifierPosition({ x: -1, y: -1 });
              }
            }}
            onTouchCancel={() => {
              if (magnifierActive) {
                setMagnifierPosition({ x: -1, y: -1 });
              }
            }}
          >
            {/* Student Waiting Screen - when teacher hasn't shared yet */}
            {userRole === 'student' && !effectivePresentationShared && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <div className="w-24 h-24 bg-brand-light rounded-full flex items-center justify-center mb-4 animate-pulse">
                  <Share2 size={40} className="text-brand-sage" />
                </div>
                <p className="text-brand-primary font-medium text-lg">{tt('awaitingPresentation')}</p>
                <p className="text-brand-taupe text-sm mt-2">{tt('awaitingPresentationSub')}</p>
              </div>
            )}

            {/* Student - teacher shared but no presentation attached to this lesson */}
            {userRole === 'student' && effectivePresentationShared && !classPresentation && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <div className="w-24 h-24 bg-brand-light rounded-full flex items-center justify-center mb-4">
                  <BookOpen size={40} className="text-brand-taupe" />
                </div>
                <p className="text-brand-primary font-medium text-lg">{tt('presentationUnavailable')}</p>
                <p className="text-brand-taupe text-sm mt-2">{tt('presentationUnavailableSub')}</p>
              </div>
            )}

            {/* Loading state - when presentation is loading */}
            {(userRole === 'teacher' || effectivePresentationShared) && !classSlide && classPresentation && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <div className="w-12 h-12 border-4 border-brand-sage border-t-brand-primary rounded-full animate-spin mb-4" />
                <p className="text-brand-primary font-medium">{tt('loadingSlide')}</p>
              </div>
            )}

            {/* Presentation is attached to the lesson but not yet loaded — show a spinner
                instead of the "not selected" empty state so the teacher doesn't see
                a false "не обрана" flash while the fetch is in flight. If the fetch
                failed (403/404/network), swap the spinner for an error so we don't
                spin forever. */}
            {userRole === 'teacher' && !classPresentation && currentLessonForClass?.presentationId && !presentationLoadError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <div className="w-12 h-12 border-4 border-brand-sage border-t-brand-primary rounded-full animate-spin mb-4" />
                <p className="text-brand-primary font-medium">{tt('loadingPresentation')}</p>
              </div>
            )}

            {userRole === 'teacher' && !classPresentation && currentLessonForClass?.presentationId && presentationLoadError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20 px-6 text-center">
                <div className="w-24 h-24 bg-brand-light rounded-full flex items-center justify-center mb-4">
                  <BookOpen size={40} className="text-brand-taupe" />
                </div>
                <p className="text-brand-primary font-medium text-lg">{tt('presentationLoadError')}</p>
                <p className="text-brand-taupe text-sm mt-2">{tt('presentationLoadErrorSub')}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:opacity-90"
                >
                  {tt('refresh')}
                </button>
              </div>
            )}

            {/* No presentation attached to this lesson at all */}
            {userRole === 'teacher' && !classPresentation && !currentLessonForClass?.presentationId && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <div className="w-24 h-24 bg-brand-light rounded-full flex items-center justify-center mb-4">
                  <BookOpen size={40} className="text-brand-taupe" />
                </div>
                <p className="text-brand-primary font-medium text-lg">{tt('presentationNotSelected')}</p>
                <p className="text-brand-taupe text-sm mt-2">{tt('presentationNotSelectedSub')}</p>
              </div>
            )}

            {/* Slide indicator - visible to teacher only.
                Mobile: bottom-left so it doesn't collide with the right-side toolbar rail or
                the top-left video PiP. Desktop: original top-right. */}
            {userRole === 'teacher' && classPresentation && (
              <div className="absolute bottom-2 left-2 md:top-3 md:right-3 md:bottom-auto md:left-auto bg-brand-primary/90 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded z-10">
                {tt('slideIndicator', { current: effectiveSlideIndex + 1, total: classPresentation?.slides?.length || 1 })}
              </div>
            )}

            {/* Timer Display - visible to both teacher and students */}
            {(timerActive || timerJustEnded) && (
              <div className={`absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg ${
                timerJustEnded
                  ? 'bg-brand-red animate-pulse'
                  : timerRemainingSeconds <= 10
                    ? 'bg-brand-red/80'
                    : 'bg-brand-primary/90'
              }`}>
                <Timer size={18} className="text-white" />
                <span className={`text-white font-mono text-lg font-bold ${
                  timerRemainingSeconds <= 10 && !timerJustEnded ? 'animate-pulse' : ''
                }`}>
                  {timerJustEnded ? tt('timeUp') : formatTime(timerRemainingSeconds)}
                </span>
                {userRole === 'teacher' && timerActive && (
                  <button
                    onClick={handleStopTimer}
                    className="ml-1 p-1 hover:bg-white/20 rounded transition"
                    title={tt('stopTimer')}
                  >
                    <Square size={14} className="text-white" />
                  </button>
                )}
              </div>
            )}

            {/* Dynamic Slide Elements — rendered inside a fixed 960×540 reference container
                that gets CSS-transform-scaled to fit the board. Mirrors ScaledSlideRenderer
                / SlideThumbnail so the slide layout is pixel-identical on every viewport
                (text wraps the same on mobile as desktop, exercise children stay proportional)
                instead of each style value being individually multiplied. */}
            {(userRole === 'teacher' || effectivePresentationShared) && classSlide?.elements && (
            <div
              ref={slideContentsRef}
              className="absolute top-0 left-0"
              style={{
                width: `${REFERENCE_WIDTH}px`,
                height: `${REFERENCE_HEIGHT}px`,
                transform: `scale(${boardScale})`,
                transformOrigin: 'top left',
                // Reset the scale-compensating CSS var inherited from the board wrapper.
                // Inside this transformed container, values are already reference-sized.
                '--slide-scale': 1,
              }}
            >
            {(() => {
              // Shadow the outer scaleValue/boardScale — inside this transformed container,
              // style values render at reference size and are scaled uniformly by the parent
              // CSS transform. Passing the outer boardScale into exercise child components
              // (Anagram/Boggle/MatchLines) would double-scale their internal pixel sizes.
              // eslint-disable-next-line no-shadow, no-unused-vars
              const scaleValue = (v) => v;
              // eslint-disable-next-line no-shadow, no-unused-vars
              const boardScale = 1;
              return classSlide.elements.map((element) => {
              // Hide anything dropped into a smittnik on this slide.
              const trashedSet = trashedElements[`${slideIndexForPositions}`];
              if (trashedSet && trashedSet.has(element.id)) return null;
              // Smart position resolution with conflict handling:
              // - If we're actively dragging (have recent local timestamp), use local position
              // - Otherwise, prefer synced position if it was updated by someone else
              // - Fall back to original element position
              const localPos = studentElementPositions[element.id];
              const syncedPos = syncedElementPositions[element.id];
              const localDragTime = localDragTimestamps[element.id] || 0;
              const isActivelyDragging = localDragTime > 0 && (Date.now() - localDragTime < 1500);

              let position;
              if (isActivelyDragging && localPos) {
                // We're actively dragging - use local position for immediate feedback
                position = localPos;
              } else if (syncedPos && syncedPos.x !== undefined) {
                // Use synced position from server (may have been updated by other user)
                position = { x: syncedPos.x, y: syncedPos.y };
              } else if (localPos) {
                // Fall back to local position if no synced position
                position = localPos;
              } else {
                // Fall back to original element position
                position = element.position;
              }

              // Teachers can drag elements that students can move, students can only drag if allowed.
              // Grouped elements are movable-as-a-unit: if any member of the group is marked
              // movable, every member responds to drag (matches preview semantics).
              const isMovable = element.groupId
                ? classSlide.elements.some(el => el.groupId === element.groupId && el.behavior?.canStudentMove)
                : !!element.behavior?.canStudentMove;
              const canDrag = isMovable; // Both teacher and student can drag movable elements
              const isEditable = element.behavior.canStudentEdit;
              const hasSlideLink = element.actions?.some(a => a.type === 'slideLink');

              // Highlight (glow) — read runtime on/off from synced interactive state
              const highlightSync = syncedInteractiveStates[element.id];
              const highlightRuntimeOn = highlightSync?.elementType === 'highlight' && !!highlightSync?.state?.on;
              const highlightShadow = getHighlightBoxShadow(element, { runtimeOn: highlightRuntimeOn });
              const highlightClickable = isHighlightClickable(element);

              const handleHighlightClick = (e) => {
                if (!highlightClickable) return;
                e.stopPropagation();
                const mode = element.style.highlight.mode;
                // click-once forces on; click-toggle flips
                const newOn = mode === 'click-once' ? true : !highlightRuntimeOn;
                setLocalInteractiveState(element.id, 'highlight', { on: newOn }, user.id);
                sendInteractiveState(lessonSessionId, element.id, 'highlight', { on: newOn }, slideIndexForPositions, user.id);
              };

              // Check if this element is currently being dragged
              const isBeingDragged = draggingElementId === element.id;

              // Labels-on-slide / puzzle widgets are locked to full slide:
              // palette/slot coords are stored as % of the slide, so the
              // wrapper must span 100×100 centered regardless of persisted
              // position/size. Matches isPassThroughOverlay in SlideCanvas.
              const isPassThroughOverlay = element.type === 'dragLabelBlankExercise' || element.type === 'puzzleExercise';

              return (
                <div
                  key={element.id}
                  onMouseDown={canDrag ? (e) => handleDragStart(e, element.id, canDrag) : undefined}
                  onTouchStart={canDrag ? (e) => handleDragStart(e, element.id, canDrag) : undefined}
                  onClick={(e) => {
                    if (highlightClickable) handleHighlightClick(e);
                    else if (hasSlideLink) handleSlideLinkClick(element, e);
                  }}
                  className={`absolute ${
                    // Remove transitions during drag for smooth movement
                    isBeingDragged ? '' : 'transition-all duration-150'
                  } ${
                    canDrag ? 'cursor-grab active:cursor-grabbing' : ((hasSlideLink || highlightClickable) ? 'cursor-pointer hover:shadow-lg' : '')
                  }`}
                  style={{
                    left: isPassThroughOverlay ? '50%' : `${position.x}%`,
                    top: isPassThroughOverlay ? '50%' : `${position.y}%`,
                    transform: isPassThroughOverlay
                      ? 'translate(-50%, -50%)'
                      : `translate(-50%, -50%)${element.style?.rotation ? ` rotate(${element.style.rotation}deg)` : ''}${element.content?.flipHorizontal ? ' scaleX(-1)' : ''}${element.content?.flipVertical ? ' scaleY(-1)' : ''}`,
                    width: isPassThroughOverlay ? '100%' : `${element.size.width}%`,
                    height: isPassThroughOverlay ? '100%' : `${element.size.height}%`,
                    backgroundColor: isPassThroughOverlay ? 'transparent' : (element.type === 'shape' || element.type === 'highlight' ? 'transparent' : element.style.backgroundColor),
                    borderRadius: element.type === 'shape' ? 0 : scaleValue(element.style.borderRadius),
                    opacity: element.style.opacity,
                    padding: element.type === 'text' ? `${scaleValue(8)}px ${scaleValue(16)}px` : '0',
                    overflow: (element.type === 'text' || element.type === 'arrow' || element.type === 'shape') ? 'visible' : 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isPassThroughOverlay ? undefined : (highlightShadow || undefined),
                    pointerEvents: isPassThroughOverlay ? 'none' : undefined,
                    // Prevent the browser from taking over touch gestures (scroll,
                    // long-press context menu) when this element is draggable.
                    touchAction: canDrag ? 'none' : undefined,
                    userSelect: canDrag ? 'none' : undefined,
                    WebkitUserSelect: canDrag ? 'none' : undefined
                  }}
                >
                  {/* Element content */}
                  {element.type === 'text' && !isEditable && (() => {
                    const textStyle = {
                      fontSize: scaleValue(element.content.fontSize),
                      fontFamily: element.content.fontFamily || 'Inter',
                      fontWeight: element.content.fontWeight,
                      fontStyle: element.content.fontStyle || 'normal',
                      color: element.content.color,
                      textAlign: element.content.alignment,
                      lineHeight: element.content.lineHeight || 1.4,
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      width: '100%'
                    };
                    return element.content.html
                      ? <div className="slide-text-html" style={textStyle} dangerouslySetInnerHTML={{ __html: element.content.html }} />
                      : <p style={textStyle}>{element.content.text || element.content.placeholder}</p>;
                  })()}

                  {element.type === 'text' && isEditable && userRole === 'student' && (
                    <div className="w-full relative">
                      {(() => {
                        const teacherAnswer = teacherAnswersMap?.answers?.[element.id];

                        // If teacher has edited this field, show teacher's answer (read-only for student)
                        if (teacherAnswer !== undefined && teacherAnswer !== '') {
                          return (
                            <div
                              className="bg-transparent border-b-2 border-brand-sage px-2 py-1"
                              style={{
                                fontSize: scaleValue(element.content.fontSize),
                                fontFamily: element.content.fontFamily || 'Inter',
                                fontStyle: element.content.fontStyle || 'normal',
                                color: element.content.color,
                                width: '100%'
                              }}
                            >
                              {teacherAnswer}
                            </div>
                          );
                        }

                        // Otherwise, show input for student to type
                        return (
                          <input
                            type="text"
                            placeholder={element.content.placeholder || tt('inputPlaceholder')}
                            value={studentInputValues[element.id] || ''}
                            onChange={(e) => setStudentInputValues(prev => ({ ...prev, [element.id]: e.target.value }))}
                            className="bg-transparent border-b-2 border-brand-sage focus:border-brand-primary outline-none px-2 py-1"
                            style={{
                              fontSize: scaleValue(element.content.fontSize),
                              fontFamily: element.content.fontFamily || 'Inter',
                              fontStyle: element.content.fontStyle || 'normal',
                              color: element.content.color,
                              width: '100%'
                            }}
                          />
                        );
                      })()}
                    </div>
                  )}

                  {/* Teacher view: editable input + show student answers */}
                  {element.type === 'text' && isEditable && userRole === 'teacher' && (
                    <div className="w-full relative">
                      {(() => {
                        const firstStudentData = Object.values(studentAnswersMap)[0];
                        const studentAnswer = firstStudentData?.answers?.[element.id];
                        // Show student answer if teacher hasn't typed anything, otherwise show teacher's input
                        const displayValue = studentInputValues[element.id] !== undefined
                          ? studentInputValues[element.id]
                          : (studentAnswer || '');

                        return (
                          <input
                            type="text"
                            placeholder={element.content.placeholder || tt('inputPlaceholder')}
                            value={displayValue}
                            onChange={(e) => setStudentInputValues(prev => ({ ...prev, [element.id]: e.target.value }))}
                            className="bg-transparent border-b-2 border-brand-sage focus:border-brand-primary outline-none px-2 py-1"
                            style={{
                              fontSize: scaleValue(element.content.fontSize),
                              fontFamily: element.content.fontFamily || 'Inter',
                              fontStyle: element.content.fontStyle || 'normal',
                              color: element.content.color,
                              width: '100%'
                            }}
                          />
                        );
                      })()}
                    </div>
                  )}

                  {element.type === 'shape' && (
                    <div className="w-full h-full flex items-center justify-center">
                      {(element.content.shapeType === 'rectangle' || !element.content.shapeType) && (
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundColor: element.content.fill,
                            border: `${scaleValue(element.content.strokeWidth)}px solid ${element.content.stroke}`,
                            borderRadius: scaleValue(element.style.borderRadius)
                          }}
                        />
                      )}
                      {element.content.shapeType === 'square' && (
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundColor: element.content.fill,
                            border: `${scaleValue(element.content.strokeWidth)}px solid ${element.content.stroke}`,
                            borderRadius: scaleValue(element.style?.borderRadius || 0)
                          }}
                        />
                      )}
                      {element.content.shapeType === 'circle' && (
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundColor: element.content.fill,
                            border: `${scaleValue(element.content.strokeWidth)}px solid ${element.content.stroke}`,
                            borderRadius: '50%'
                          }}
                        />
                      )}
                      {element.content.shapeType === 'oval' && (
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundColor: element.content.fill,
                            border: `${scaleValue(element.content.strokeWidth)}px solid ${element.content.stroke}`,
                            borderRadius: '50%'
                          }}
                        />
                      )}
                      {element.content.shapeType === 'triangle' && (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <polygon
                            points="50,5 95,95 5,95"
                            fill={element.content.fill}
                            stroke={element.content.stroke}
                            strokeWidth={element.content.strokeWidth}
                          />
                        </svg>
                      )}
                      {element.content.shapeType === 'messageBubble' && (
                        <svg viewBox="0 0 100 80" className="w-full h-full" preserveAspectRatio="none">
                          <path
                            d="M 10 5 L 90 5 Q 95 5 95 10 L 95 55 Q 95 60 90 60 L 30 60 L 15 75 L 20 60 L 10 60 Q 5 60 5 55 L 5 10 Q 5 5 10 5"
                            fill={element.content.fill}
                            stroke={element.content.stroke}
                            strokeWidth={element.content.strokeWidth}
                          />
                        </svg>
                      )}
                      {element.content.shapeType === 'roundedMessageBubble' && (
                        <svg viewBox="0 0 100 80" className="w-full h-full" preserveAspectRatio="none">
                          <path
                            d="M 20 5 L 80 5 Q 95 5 95 20 L 95 50 Q 95 65 80 65 L 40 65 L 25 80 L 35 65 L 20 65 Q 5 65 5 50 L 5 20 Q 5 5 20 5"
                            fill={element.content.fill}
                            stroke={element.content.stroke}
                            strokeWidth={element.content.strokeWidth}
                          />
                        </svg>
                      )}
                    </div>
                  )}

                  {element.type === 'image' && (
                    <div className={`w-full h-full relative overflow-hidden rounded ${element.content.src ? '' : 'bg-gray-200 flex items-center justify-center min-w-[80px] min-h-[60px]'}`}>
                      {element.content.src ? (() => {
                        const hasValidContentSize = element.contentSize &&
                          element.contentSize.width > 0 &&
                          element.contentSize.height > 0 &&
                          element.size.width > 0 &&
                          element.size.height > 0;

                        const imgWidth = hasValidContentSize
                          ? (element.contentSize.width / element.size.width) * 100
                          : 100;
                        const imgHeight = hasValidContentSize
                          ? (element.contentSize.height / element.size.height) * 100
                          : 100;

                        const imagePos = element.imagePosition || {};
                        let bgPosX = 50;
                        let bgPosY = 50;

                        if (hasValidContentSize) {
                          if (imagePos.x !== undefined) {
                            const offsetX = imagePos.x - element.position.x;
                            const extraWidth = element.contentSize.width - element.size.width;
                            if (extraWidth > 0.01) {
                              bgPosX = 50 - (offsetX * 100 / extraWidth);
                              bgPosX = Math.max(0, Math.min(100, bgPosX));
                            }
                          } else if (imagePos.horizontal === 'left') {
                            bgPosX = 0;
                          } else if (imagePos.horizontal === 'right') {
                            bgPosX = 100;
                          }

                          if (imagePos.y !== undefined) {
                            const offsetY = imagePos.y - element.position.y;
                            const extraHeight = element.contentSize.height - element.size.height;
                            if (extraHeight > 0.01) {
                              bgPosY = 50 - (offsetY * 100 / extraHeight);
                              bgPosY = Math.max(0, Math.min(100, bgPosY));
                            }
                          } else if (imagePos.vertical === 'top') {
                            bgPosY = 0;
                          } else if (imagePos.vertical === 'bottom') {
                            bgPosY = 100;
                          }
                        }

                        return (
                          <GifImageLayer
                            src={getMediaUrl(element.content.src)}
                            mode={element.content.gifPlayback}
                            canTrigger={userRole === 'teacher'}
                            syncedPlay={syncedInteractiveStates[element.id]?.state || null}
                            onPlay={(next) => handleLiveGameStateChange(element.id, 'gifPlay', next)}
                            style={{
                              backgroundSize: hasValidContentSize
                                ? `${imgWidth}% ${imgHeight}%`
                                : 'contain',
                              backgroundPosition: `${bgPosX}% ${bgPosY}%`,
                              backgroundRepeat: 'no-repeat',
                              filter: getImageFilterCss(element.style?.filters)
                            }}
                          />
                        );
                      })() : (
                        <Image size={24} className="text-gray-400" />
                      )}
                    </div>
                  )}
                  {element.type === 'frame' && (
                    <FrameElement
                      element={element}
                      mode="view"
                      isTeacher={userRole === 'teacher'}
                      lessonSessionId={lessonSessionId}
                    />
                  )}
                  {element.type === 'sticker' && element.content?.src && (
                    <img
                      src={getMediaUrl(element.content.src)}
                      alt={element.content.alt || 'Sticker'}
                      className="w-full h-full object-contain pointer-events-none"
                      draggable={false}
                    />
                  )}
                  {element.type === 'video' && (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center min-w-[120px] min-h-[80px] rounded">
                      {element.content.src ? (
                        <SyncedVideo
                          elementId={element.id}
                          src={getMediaUrl(element.content.src)}
                          className="w-full h-full object-cover"
                          isTeacher={userRole === 'teacher'}
                          lessonSessionId={lessonSessionId}
                          initialMuted={false}
                          loop={element.content.loop}
                          autoPlay={element.content.autoplay}
                          trimStart={element.content.trimStart}
                          trimEnd={element.content.trimEnd}
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Video size={24} className="text-gray-400" />
                          <span className="text-xs text-gray-400">{tt('videoAlt')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {element.type === 'youtubeVideo' && (
                    <div className="w-full h-full bg-black flex items-center justify-center min-w-[120px] min-h-[80px] rounded overflow-hidden">
                      {element.content?.videoId ? (
                        <SyncedYouTubeVideo
                          elementId={element.id}
                          videoId={element.content.videoId}
                          className="w-full h-full"
                          isTeacher={userRole === 'teacher'}
                          lessonSessionId={lessonSessionId}
                          autoplay={element.content.autoplay}
                          mute={element.content.mute}
                          loop={element.content.loop}
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Youtube size={24} className="text-red-500" />
                          <span className="text-xs text-gray-300">YouTube</span>
                        </div>
                      )}
                    </div>
                  )}
                  {element.type === 'audio' && (
                    <div className="w-full h-full bg-gradient-to-r from-brand-primary to-brand-secondary flex items-center justify-center min-w-[150px] min-h-[50px] rounded px-3">
                      {element.content.src ? (
                        <SyncedAudio
                          elementId={element.id}
                          src={getMediaUrl(element.content.src)}
                          className="w-full"
                          isTeacher={userRole === 'teacher'}
                          lessonSessionId={lessonSessionId}
                          loop={element.content.loop}
                          autoPlay={element.content.autoplay}
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <Music size={20} className="text-white" />
                          <span className="text-sm text-white">{tt('audioAlt')}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sound element — short clip, click to play, no controls */}
                  {element.type === 'sound' && (
                    <SoundElement element={element} interactive={true} />
                  )}

                  {/* Arrow element */}
                  {element.type === 'arrow' && (
                    <ArrowElement element={element} />
                  )}

                  {/* Input Field element */}
                  {element.type === 'inputField' && (
                    <InputFieldElement
                      element={element}
                      isEditor={false}
                      value={getInteractiveState(element.id, 'inputField') || ''}
                      onChange={(val) => handleInputFieldChange(element.id, val)}
                      disabled={userRole === 'teacher'}
                    />
                  )}

                  {/* Notebook element — student writes on lines; teacher watches live. */}
                  {element.type === 'notebook' && (
                    <NotebookElement
                      element={element}
                      isEditor={false}
                      value={getInteractiveState(element.id, 'notebook') || ''}
                      onChange={(val) => handleNotebookChange(element.id, val)}
                      disabled={userRole === 'teacher'}
                    />
                  )}

                  {/* Spin Wheel element */}
                  {element.type === 'spinWheel' && (
                    <SpinWheelElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onResult={(result) => handleWheelSpin(element.id, result)}
                      syncedRotation={getInteractiveState(element.id, 'spinWheel')}
                    />
                  )}

                  {/* Flip Card element */}
                  {element.type === 'flipCard' && (
                    <FlipCardElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onFlip={(flipped) => handleFlipCard(element.id, flipped)}
                      syncedFlipped={getInteractiveState(element.id, 'flipCard')}
                    />
                  )}

                  {/* Dice element */}
                  {element.type === 'dice' && (
                    <DiceElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onRoll={(val) => handleDiceRoll(element.id, val)}
                      syncedValue={getInteractiveState(element.id, 'dice')}
                    />
                  )}

                  {/* Alias game — role-based split (learner explains turn 1, teacher explains turn 2) */}
                  {element.type === 'aliasGame' && (
                    <AliasElement
                      element={element}
                      userRole={userRole}
                      gameState={syncedInteractiveStates[element.id]?.state || null}
                      onGameStateChange={(next) => handleAliasStateChange(element.id, next)}
                    />
                  )}

                  {/* Taboo — same turn flow as Alias; explainer sees word + forbidden list */}
                  {element.type === 'tabooGame' && (
                    <TabooElement
                      element={element}
                      userRole={userRole}
                      gameState={syncedInteractiveStates[element.id]?.state || null}
                      onGameStateChange={(next) => handleLiveGameStateChange(element.id, 'tabooGame', next)}
                    />
                  )}

                  {/* Who Am I? — asymmetric: student sees hidden rectangle, teacher sees image + controls */}
                  {element.type === 'whoAmIGame' && (
                    <WhoAmIElement
                      element={element}
                      userRole={userRole}
                      gameState={syncedInteractiveStates[element.id]?.state || null}
                      onGameStateChange={(next) => handleWhoAmIStateChange(element.id, next)}
                    />
                  )}

                  {/* Checkbox element */}
                  {element.type === 'checkbox' && (
                    <CheckboxElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onToggle={(checked) => handleCheckboxToggle(element.id, checked)}
                      syncedChecked={getInteractiveState(element.id, 'checkbox')}
                    />
                  )}

                  {/* Like element */}
                  {element.type === 'like' && (
                    <LikeElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onToggle={(liked) => handleLikeToggle(element.id, liked)}
                      syncedLiked={getInteractiveState(element.id, 'like')}
                    />
                  )}

                  {/* Rating element */}
                  {element.type === 'rating' && (
                    <RatingElement
                      element={element}
                      isEditor={false}
                      canInteract={true}
                      onRate={(rating) => handleRatingChange(element.id, rating)}
                      syncedRating={getInteractiveState(element.id, 'rating')}
                    />
                  )}

                  {/* Shop element (price tags + budget) */}
                  {element.type === 'shop' && (() => {
                    const synced = getInteractiveState(element.id, 'shop');
                    return (
                      <ShopElement
                        element={element}
                        isEditor={false}
                        canInteract={userRole === 'student'}
                        disabled={userRole === 'teacher'}
                        onPurchasedChange={(ids) => handleShopPurchasedChange(element.id, ids)}
                        syncedPurchasedIds={Array.isArray(synced) ? synced : []}
                      />
                    );
                  })()}

                  {/* Card deck element (flip and move between decks) */}
                  {element.type === 'cardDeck' && (() => {
                    const synced = getInteractiveState(element.id, 'cardDeck');
                    return (
                      <CardDeckElement
                        element={element}
                        isEditor={false}
                        canInteract={userRole === 'student'}
                        disabled={userRole === 'teacher'}
                        onMovedChange={(ids) => handleCardDeckMovedChange(element.id, ids)}
                        syncedMovedIds={Array.isArray(synced) ? synced : []}
                      />
                    );
                  })()}

                  {/* Book — teacher and student flip pages together, synced live */}
                  {element.type === 'book' && (() => {
                    const synced = getInteractiveState(element.id, 'book');
                    return (
                      <BookElement
                        element={element}
                        isEditor={false}
                        canInteract={true}
                        onFlippedCountChange={(count) => handleBookFlippedChange(element.id, count)}
                        syncedFlippedCount={typeof synced === 'number' ? synced : 0}
                      />
                    );
                  })()}

                  {/* Highlight overlay — hidden shape reveals when the student taps */}
                  {element.type === 'highlight' && (() => {
                    const synced = getInteractiveState(element.id, 'highlight');
                    return (
                      <HighlightElement
                        element={element}
                        isEditor={false}
                        canInteract={true}
                        disabled={false}
                        syncedRevealed={typeof synced === 'boolean' ? synced : false}
                        onReveal={(next) => handleHighlightReveal(element.id, next)}
                      />
                    );
                  })()}

                  {/* Smittnik (trash bin) — passive drop zone. Movable items
                      dropped onto it keep their coordinates via the generic
                      student-drag pipeline, so they visually stay inside. */}
                  {element.type === 'smittnik' && (
                    <SmittnikElement element={element} />
                  )}

                  {/* Bingo grid — student taps cells to cross them out. */}
                  {element.type === 'bingo' && (() => {
                    const synced = getInteractiveState(element.id, 'bingo');
                    return (
                      <BingoElement
                        element={element}
                        isEditor={false}
                        canInteract={userRole === 'student'}
                        onCrossedChange={(ids) => handleBingoCrossedChange(element.id, ids)}
                        syncedCrossedIds={Array.isArray(synced) ? synced : []}
                      />
                    );
                  })()}

                  {/* Memory game — student flips cards to find pairs. Local
                      state per viewer (no sync); teacher sees empty board. */}
                  {element.type === 'memory' && (
                    <MemoryElement
                      element={element}
                      isEditor={false}
                      disabled={userRole === 'teacher'}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Kiss / Marry / Avoid — student picks, teacher watches live. */}
                  {element.type === 'kissMarryAvoid' && (() => {
                    const synced = getInteractiveState(element.id, 'kissMarryAvoid');
                    return (
                      <KissMarryAvoidElement
                        element={element}
                        isEditor={false}
                        canInteract={userRole === 'student'}
                        onPicksChange={(picks) => handleKissMarryAvoidChange(element.id, picks)}
                        syncedPicks={synced && typeof synced === 'object' ? synced : {}}
                        slideScale={boardScale}
                      />
                    );
                  })()}

                  {/* Board game (ходилка) — both roles roll; state synced. */}
                  {element.type === 'boardGame' && (
                    <BoardGameElement
                      element={element}
                      canInteract={true}
                      syncedState={syncedInteractiveStates[element.id]?.state || null}
                      onStateChange={(next) => handleLiveGameStateChange(element.id, 'boardGame', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Dress-up — student picks outfits, teacher watches live. */}
                  {element.type === 'dressUp' && (
                    <DressUpElement
                      element={element}
                      canInteract={userRole === 'student'}
                      syncedState={syncedInteractiveStates[element.id]?.state || null}
                      onStateChange={(next) => handleLiveGameStateChange(element.id, 'dressUp', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Spot the difference — student finds, teacher watches with answers outlined. */}
                  {element.type === 'spotDifference' && (
                    <SpotDifferenceElement
                      element={element}
                      canInteract={userRole === 'student'}
                      showAnswers={userRole === 'teacher'}
                      syncedState={syncedInteractiveStates[element.id]?.state || null}
                      onStateChange={(next) => handleLiveGameStateChange(element.id, 'spotDifference', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Mind map — student and teacher can both add ideas. */}
                  {element.type === 'mindMap' && (
                    <MindMapElement
                      element={element}
                      canInteract={true}
                      syncedState={syncedInteractiveStates[element.id]?.state || null}
                      onStateChange={(next) => handleLiveGameStateChange(element.id, 'mindMap', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Dialogue simulator — student picks replies, teacher watches live. */}
                  {element.type === 'dialogueExercise' && (
                    <DialogueElement
                      element={element}
                      canInteract={userRole === 'student'}
                      syncedState={syncedInteractiveStates[element.id]?.state || null}
                      onStateChange={(next) => handleLiveGameStateChange(element.id, 'dialogueExercise', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Pictionary — game state and drawing synced on separate channels. */}
                  {element.type === 'pictionaryGame' && (
                    <PictionaryElement
                      element={element}
                      userRole={userRole}
                      gameState={syncedInteractiveStates[element.id]?.state || null}
                      onGameStateChange={(next) => handleLiveGameStateChange(element.id, 'pictionaryGame', next)}
                      strokesState={syncedInteractiveStates[`${element.id}__strokes`]?.state || null}
                      onStrokesChange={(next) => handleLiveGameStateChange(`${element.id}__strokes`, 'pictionaryStrokes', next)}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Personality quiz — local state per viewer (no sync). */}
                  {element.type === 'personalityQuiz' && (
                    <PersonalityQuizElement
                      element={element}
                      isEditor={false}
                      slideScale={boardScale}
                    />
                  )}

                  {/* Table element (read-only in classroom) */}
                  {element.type === 'table' && (
                    <TableElement element={element} isEditor={false} />
                  )}

                  {/* Exercise elements */}
                  {(element.type === 'singleChoiceExercise' || element.type === 'multipleChoiceExercise') && (() => {
                    const state = getExerciseState(element.id);
                    return (
                      <ExerciseElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        selectedAnswers={state.selectedAnswers || []}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        correctAnswers={state.correctAnswers}
                        onSelectAnswer={(answers) => handleExerciseSelect(element.id, answers)}
                        onCheck={(answers) => handleExerciseCheck(element, answers)}
                        onRetry={() => handleExerciseRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Anagram exercise (letter-tile unscramble) */}
                  {element.type === 'anagramExercise' && (() => {
                    const state = getAnagramState(element.id);
                    return (
                      <AnagramElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentArrangement={state.arrangement || null}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        onArrangementChange={(arr) => handleAnagramArrangementChange(element.id, arr)}
                        onCheck={(word) => handleAnagramCheck(element, word)}
                        onRetry={() => handleAnagramRetry(element.id)}
                        disabled={false}
                        scale={boardScale}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Boggle exercise (find words by connecting adjacent letters) */}
                  {element.type === 'boggleExercise' && (() => {
                    const state = getBoggleState(element.id);
                    return (
                      <BoggleElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentState={{
                          path: state.path || [],
                          foundWords: state.foundWords || [],
                          startedAt: state.startedAt || 0,
                        }}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        onStateChange={(s) => handleBoggleStateChange(element.id, s)}
                        onCheck={(result) => handleBoggleCheck(element, result)}
                        onRetry={() => handleBoggleRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        scale={boardScale}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Word pool exercise (find words from a letter pool, no adjacency) */}
                  {element.type === 'wordPoolExercise' && (() => {
                    const state = getBoggleState(element.id);
                    return (
                      <WordPoolElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentState={{
                          path: state.path || [],
                          foundWords: state.foundWords || [],
                          startedAt: state.startedAt || 0,
                        }}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        onStateChange={(s) => handleBoggleStateChange(element.id, s)}
                        onCheck={(result) => handleBoggleCheck(element, result)}
                        onRetry={() => handleBoggleRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        scale={boardScale}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Puzzle exercise — self-contained, no external state. */}
                  {element.type === 'puzzleExercise' && (
                    <PuzzleElement
                      element={element}
                      isEditor={false}
                      studentView={userRole === 'student'}
                    />
                  )}

                  {/* Find-object exercise — self-contained, no external state. */}
                  {element.type === 'findObjectExercise' && (
                    <FindObjectElement
                      element={element}
                      isEditor={false}
                      studentView={userRole === 'student'}
                    />
                  )}

                  {/* NMT-image exercise — self-contained, no external state. */}
                  {element.type === 'nmtImageExercise' && (
                    <NmtImageElement
                      element={element}
                      isEditor={false}
                      studentView={userRole === 'student'}
                    />
                  )}

                  {/* Find-sticker exercise — controlled state so the teacher's
                      mirrored view shows the student's progress live. */}
                  {element.type === 'findStickerExercise' && (() => {
                    const state = getFindStickerState(element.id);
                    return (
                      <FindStickerElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        foundIds={state.foundIds || []}
                        idx={state.idx ?? 0}
                        onFound={(next) => handleFindStickerChange(element, next)}
                        onRetry={() => handleFindStickerRetry(element.id)}
                        disabled={userRole === 'teacher'}
                      />
                    );
                  })()}

                  {/* Drag-label exercises (image / text / blank canvas) */}
                  {(element.type === 'dragLabelImageExercise' || element.type === 'dragLabelTextExercise' || element.type === 'dragLabelBlankExercise') && (() => {
                    const state = getDragLabelState(element.id);
                    const Comp = element.type === 'dragLabelImageExercise' ? DragLabelImageElement
                      : element.type === 'dragLabelTextExercise' ? DragLabelTextElement
                      : DragLabelBlankElement;
                    return (
                      <Comp
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        placements={state.placements || null}
                        showResult={state.showResult || false}
                        perSlotResults={state.perSlotResults || null}
                        onPlacementsChange={(p) => handleDragLabelPlacementsChange(element.id, p)}
                        onCheck={(p) => handleDragLabelCheck(element, p)}
                        onRetry={() => handleDragLabelRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Match-lines exercise */}
                  {element.type === 'matchLinesExercise' && (() => {
                    const state = getMatchLinesState(element.id);
                    return (
                      <MatchLinesElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        connections={state.connections || null}
                        showResult={state.showResult || false}
                        perPairResults={state.perPairResults || null}
                        onConnectionsChange={(c) => handleMatchLinesConnectionsChange(element.id, c)}
                        onCheck={(c) => handleMatchLinesCheck(element, c)}
                        onRetry={() => handleMatchLinesRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                        slideScale={boardScale}
                      />
                    );
                  })()}

                  {/* Label-image exercise */}
                  {element.type === 'labelImageExercise' && (() => {
                    const state = getLabelImageState(element.id);
                    return (
                      <LabelImageElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        connections={state.connections || null}
                        showResult={state.showResult || false}
                        perItemResults={state.perItemResults || null}
                        onConnectionsChange={(c) => handleLabelImageConnectionsChange(element.id, c)}
                        onCheck={(c) => handleLabelImageCheck(element, c)}
                        onRetry={() => handleLabelImageRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                        slideScale={boardScale}
                      />
                    );
                  })()}

                  {/* Odd-one-out exercise */}
                  {element.type === 'oddOneOutExercise' && (() => {
                    const state = getOddOneOutState(element.id);
                    return (
                      <OddOneOutElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        selections={state.selections || {}}
                        showResult={state.showResult || false}
                        perRowResults={state.perRowResults || null}
                        onSelectionsChange={(s) => handleOddOneOutSelectionsChange(element.id, s)}
                        onCheck={(s) => handleOddOneOutCheck(element, s)}
                        onRetry={() => handleOddOneOutRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Sorting exercise */}
                  {element.type === 'sortingExercise' && (() => {
                    const state = getSortingState(element.id);
                    return (
                      <SortingElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        placements={state.placements || {}}
                        showResult={state.showResult || false}
                        perItemResults={state.perItemResults || null}
                        onPlacementsChange={(p) => handleSortingPlacementsChange(element.id, p)}
                        onCheck={(p) => handleSortingCheck(element, p)}
                        onRetry={() => handleSortingRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Unjumble (sentence order) exercise */}
                  {element.type === 'unjumbleExercise' && (() => {
                    const state = getUnjumbleState(element.id);
                    return (
                      <UnjumbleElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentArrangement={state.arrangement || null}
                        showResult={state.showResult || false}
                        perWordResults={state.perWordResults || null}
                        isCorrect={state.isCorrect}
                        onArrangementChange={(arr) => handleUnjumbleArrangementChange(element.id, arr)}
                        onCheck={(arr) => handleUnjumbleCheck(element, arr)}
                        onRetry={() => handleUnjumbleRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Order-sentences exercise */}
                  {element.type === 'orderSentencesExercise' && (() => {
                    const state = getOrderSentencesState(element.id);
                    return (
                      <OrderSentencesElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentArrangement={state.arrangement || null}
                        showResult={state.showResult || false}
                        perItemResults={state.perItemResults || null}
                        isCorrect={state.isCorrect}
                        onArrangementChange={(arr) => handleOrderSentencesArrangementChange(element.id, arr)}
                        onCheck={(arr) => handleOrderSentencesCheck(element, arr)}
                        onRetry={() => handleOrderSentencesRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Rank (order from first to last) exercise */}
                  {element.type === 'rankExercise' && (() => {
                    const state = getRankState(element.id);
                    return (
                      <RankElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        placements={state.placements || {}}
                        showResult={state.showResult || false}
                        perItemResults={state.perItemResults || null}
                        onPlacementsChange={(p) => handleRankPlacementsChange(element.id, p)}
                        onCheck={(p) => handleRankCheck(element, p)}
                        onRetry={() => handleRankRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Type-answer exercise */}
                  {element.type === 'typeAnswerExercise' && (() => {
                    const state = getTypeAnswerState(element.id);
                    return (
                      <TypeAnswerElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentAnswer={state.answer || ''}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        onAnswerChange={(a) => handleTypeAnswerChange(element.id, a)}
                        onCheck={(a) => handleTypeAnswerCheck(element, a)}
                        onRetry={() => handleTypeAnswerRetry(element.id)}
                        disabled={userRole === 'teacher'}
                      />
                    );
                  })()}

                  {/* Missing-letters exercise */}
                  {element.type === 'missingLettersExercise' && (() => {
                    const state = getMissingLettersState(element.id);
                    return (
                      <MissingLettersElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentAnswer={state.answer || {}}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        perLetterResults={state.perLetterResults || null}
                        onAnswerChange={(a) => handleMissingLettersChange(element.id, a)}
                        onCheck={(a) => handleMissingLettersCheck(element, a)}
                        onRetry={() => handleMissingLettersRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* True/False exercise */}
                  {element.type === 'trueFalseExercise' && (() => {
                    const state = getTrueFalseState(element.id);
                    return (
                      <TrueFalseElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perStatementResults={state.perStatementResults || null}
                        onAnswersChange={(a) => handleTrueFalseAnswersChange(element.id, a)}
                        onCheck={(a) => handleTrueFalseCheck(element, a)}
                        onRetry={() => handleTrueFalseRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* ABCD exercise */}
                  {element.type === 'abcdExercise' && (() => {
                    const state = getAbcdState(element.id);
                    return (
                      <AbcdElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perQuestionResults={state.perQuestionResults || null}
                        onAnswersChange={(a) => handleAbcdAnswersChange(element.id, a)}
                        onCheck={(a) => handleAbcdCheck(element, a)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Choose-word exercise */}
                  {element.type === 'chooseWordExercise' && (() => {
                    const state = getChooseWordState(element.id);
                    return (
                      <ChooseWordElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perChoiceResults={state.perChoiceResults || null}
                        onAnswersChange={(a) => handleChooseWordAnswersChange(element.id, a)}
                        onCheck={(a) => handleChooseWordCheck(element, a)}
                        onRetry={() => handleChooseWordRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Fix-mistake exercise */}
                  {element.type === 'fixMistakeExercise' && (() => {
                    const state = getFixMistakeState(element.id);
                    return (
                      <FixMistakeElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perMistakeResults={state.perMistakeResults || null}
                        onAnswersChange={(a) => handleFixMistakeAnswersChange(element.id, a)}
                        onCheck={(a) => handleFixMistakeCheck(element, a)}
                        onRetry={() => handleFixMistakeRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Fill-gaps exercise */}
                  {element.type === 'fillGapsExercise' && (() => {
                    const state = getFillGapsState(element.id);
                    return (
                      <FillGapsElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perGapResults={state.perGapResults || null}
                        onAnswersChange={(a) => handleFillGapsAnswersChange(element.id, a)}
                        onCheck={(a) => handleFillGapsCheck(element, a)}
                        onRetry={() => handleFillGapsRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Table-fill exercise */}
                  {element.type === 'tableFillExercise' && (() => {
                    const state = getTableFillState(element.id);
                    return (
                      <TableFillElement
                        element={getStudentSafeElement(element)}
                        isEditor={false}
                        answers={state.answers || null}
                        showResult={state.showResult || false}
                        perCellResults={state.perCellResults || null}
                        onAnswersChange={(a) => handleTableFillAnswersChange(element.id, a)}
                        onCheck={(a) => handleTableFillCheck(element, a)}
                        onRetry={() => handleTableFillRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Pronounce (speak-into-mic) exercise. Auto-checks on
                      mic result — no separate "Перевірити" click. */}
                  {element.type === 'pronounceExercise' && (() => {
                    const state = getPronounceState(element.id);
                    return (
                      <PronounceElement
                        element={element}
                        isEditor={false}
                        studentView={userRole === 'student'}
                        currentState={{
                          transcript: state.transcript || '',
                          confidence: state.confidence ?? null,
                        }}
                        showResult={state.showResult || false}
                        isCorrect={state.isCorrect}
                        onStateChange={(s) => handlePronounceStateChange(element.id, s)}
                        onCheck={(r) => handlePronounceCheck(element, r)}
                        onRetry={() => handlePronounceRetry(element.id)}
                        disabled={userRole === 'teacher'}
                        hideCheckButton={true}
                      />
                    );
                  })()}

                  {/* Click-to-play sound badge for interactive tasks */}
                  {supportsSoundBadge(element.type) && element.content?.soundUrl && (
                    <ExerciseSoundBadge soundUrl={element.content.soundUrl} interactive={true} />
                  )}

                  {/* Group element - render children */}
                  {element.type === 'group' && element.children?.map((child) => (
                    <div
                      key={child.id}
                      className="absolute"
                      style={{
                        left: `${50 + child.relativePosition.x}%`,
                        top: `${50 + child.relativePosition.y}%`,
                        transform: 'translate(-50%, -50%)',
                        width: `${child.relativeSize.width}%`,
                        height: `${child.relativeSize.height}%`,
                        backgroundColor: child.type === 'shape' ? 'transparent' : (child.style?.backgroundColor || 'transparent'),
                        borderRadius: child.type === 'shape' ? 0 : scaleValue(child.style?.borderRadius ?? 8),
                        opacity: child.style?.opacity ?? 1,
                        padding: child.type === 'text' ? `${scaleValue(8)}px ${scaleValue(16)}px` : '0',
                        overflow: (child.type === 'text' || child.type === 'arrow' || child.type === 'shape') ? 'visible' : 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Text child */}
                      {child.type === 'text' && (() => {
                        const childStyle = {
                          fontSize: scaleValue(child.content.fontSize),
                          fontFamily: child.content.fontFamily || 'Inter',
                          fontWeight: child.content.fontWeight,
                          fontStyle: child.content.fontStyle || 'normal',
                          color: child.content.color,
                          textAlign: child.content.alignment,
                          lineHeight: child.content.lineHeight || 1.4,
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          width: '100%'
                        };
                        return child.content.html
                          ? <div className="slide-text-html" style={childStyle} dangerouslySetInnerHTML={{ __html: child.content.html }} />
                          : <p style={childStyle}>{child.content.text || child.content.placeholder}</p>;
                      })()}

                      {/* Shape child */}
                      {child.type === 'shape' && (
                        <div className="w-full h-full flex items-center justify-center">
                          {(child.content.shapeType === 'rectangle' || !child.content.shapeType) && (
                            <div
                              className="w-full h-full"
                              style={{
                                backgroundColor: child.content.fill,
                                border: `${scaleValue(child.content.strokeWidth)}px solid ${child.content.stroke}`,
                                borderRadius: scaleValue(child.style.borderRadius)
                              }}
                            />
                          )}
                          {child.content.shapeType === 'square' && (
                            <div
                              className="w-full h-full"
                              style={{
                                backgroundColor: child.content.fill,
                                border: `${scaleValue(child.content.strokeWidth)}px solid ${child.content.stroke}`,
                                borderRadius: scaleValue(child.style?.borderRadius || 0)
                              }}
                            />
                          )}
                          {child.content.shapeType === 'circle' && (
                            <div
                              className="w-full h-full"
                              style={{
                                backgroundColor: child.content.fill,
                                border: `${scaleValue(child.content.strokeWidth)}px solid ${child.content.stroke}`,
                                borderRadius: '50%'
                              }}
                            />
                          )}
                          {child.content.shapeType === 'oval' && (
                            <div
                              className="w-full h-full"
                              style={{
                                backgroundColor: child.content.fill,
                                border: `${scaleValue(child.content.strokeWidth)}px solid ${child.content.stroke}`,
                                borderRadius: '50%'
                              }}
                            />
                          )}
                          {child.content.shapeType === 'triangle' && (
                            <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                              <polygon
                                points="50,5 95,95 5,95"
                                fill={child.content.fill}
                                stroke={child.content.stroke}
                                strokeWidth={child.content.strokeWidth}
                              />
                            </svg>
                          )}
                          {child.content.shapeType === 'messageBubble' && (
                            <svg viewBox="0 0 100 80" className="w-full h-full" preserveAspectRatio="none">
                              <path
                                d="M 10 5 L 90 5 Q 95 5 95 10 L 95 55 Q 95 60 90 60 L 30 60 L 15 75 L 20 60 L 10 60 Q 5 60 5 55 L 5 10 Q 5 5 10 5"
                                fill={child.content.fill}
                                stroke={child.content.stroke}
                                strokeWidth={child.content.strokeWidth}
                              />
                            </svg>
                          )}
                          {child.content.shapeType === 'roundedMessageBubble' && (
                            <svg viewBox="0 0 100 80" className="w-full h-full" preserveAspectRatio="none">
                              <path
                                d="M 20 5 L 80 5 Q 95 5 95 20 L 95 50 Q 95 65 80 65 L 40 65 L 25 80 L 35 65 L 20 65 Q 5 65 5 50 L 5 20 Q 5 5 20 5"
                                fill={child.content.fill}
                                stroke={child.content.stroke}
                                strokeWidth={child.content.strokeWidth}
                              />
                            </svg>
                          )}
                        </div>
                      )}

                      {/* Image child */}
                      {child.type === 'image' && (
                        <div className={`w-full h-full flex items-center justify-center min-w-[80px] min-h-[60px] rounded ${child.content.src ? '' : 'bg-gray-200'}`}>
                          {child.content.src ? (
                            <img
                              src={getMediaUrl(child.content.src)}
                              alt={child.content.alt || tt('imageAlt')}
                              className="w-full h-full object-contain pointer-events-none"
                              draggable={false}
                            />
                          ) : (
                            <Image size={24} className="text-gray-400" />
                          )}
                        </div>
                      )}

                      {/* Sticker child */}
                      {child.type === 'sticker' && child.content?.src && (
                        <img
                          src={getMediaUrl(child.content.src)}
                          alt={child.content.alt || 'Sticker'}
                          className="w-full h-full object-contain pointer-events-none"
                          draggable={false}
                        />
                      )}

                      {/* Video child */}
                      {child.type === 'video' && (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center min-w-[120px] min-h-[80px] rounded">
                          {child.content.src ? (
                            <SyncedVideo
                              elementId={child.id}
                              src={getMediaUrl(child.content.src)}
                              className="w-full h-full object-cover"
                              isTeacher={userRole === 'teacher'}
                              lessonSessionId={lessonSessionId}
                              initialMuted={false}
                              loop={child.content.loop}
                              autoPlay={child.content.autoplay}
                              trimStart={child.content.trimStart}
                              trimEnd={child.content.trimEnd}
                            />
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <Video size={24} className="text-gray-400" />
                              <span className="text-xs text-gray-400">{tt('videoAlt')}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Audio child */}
                      {child.type === 'audio' && (
                        <div className="w-full h-full bg-gradient-to-r from-brand-primary to-brand-secondary flex items-center justify-center min-w-[150px] min-h-[50px] rounded px-3">
                          {child.content.src ? (
                            <SyncedAudio
                              elementId={child.id}
                              src={getMediaUrl(child.content.src)}
                              className="w-full"
                              isTeacher={userRole === 'teacher'}
                              lessonSessionId={lessonSessionId}
                              loop={child.content.loop}
                              autoPlay={child.content.autoplay}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Music size={20} className="text-white" />
                              <span className="text-sm text-white">{tt('audioAlt')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            });
            })()}
            </div>
            )}

            {/* Magnifier Lens - Student tool.
                In touch mode the lens is offset ~95px up so the user's finger doesn't cover
                the zoomed area they're trying to see. */}
            {magnifierActive && userRole === 'student' && effectivePresentationShared && magnifierPosition.x >= 0 && magnifierPosition.y >= 0 && (() => {
              const lensYOffset = magnifierTouchMode ? 170 : 75;
              return (
              <div
                className="pointer-events-none absolute z-30 rounded-full border-4 border-brand-primary shadow-lg overflow-hidden"
                style={{
                  width: '150px',
                  height: '150px',
                  left: magnifierPosition.x - 75,
                  top: magnifierPosition.y - lensYOffset,
                  background: classSlide?.backgroundColor?.includes('gradient')
                    ? classSlide.backgroundColor
                    : (classSlide?.backgroundColor || '#ffffff'),
                }}
              >
                <div
                  ref={magnifierLensInnerRef}
                  style={{
                    position: 'absolute',
                    width: boardRef.current ? boardRef.current.offsetWidth : '100%',
                    height: boardRef.current ? boardRef.current.offsetHeight : '100%',
                    left: 75 - (magnifierPosition.x * 2),
                    top: lensYOffset - (magnifierPosition.y * 2),
                    transform: 'scale(2)',
                    transformOrigin: 'top left',
                  }}
                />
              </div>
              );
            })()}

            {/* Drawing Canvas Overlay - visible to both teacher and students when presentation is shared */}
            {(userRole === 'teacher' || effectivePresentationShared) && (
              <DrawingCanvas
                strokes={drawingStrokes}
                activeTool={drawingTool}
                color={drawingColor}
                size={drawingSize}
                userId={user?.id}
                onStrokeComplete={handleStrokeComplete}
                onEraseStroke={handleEraseStroke}
                disabled={false}
              />
            )}

            {/* Teacher Pointer - rendered locally for the teacher (pixel coords) */}
            {userRole === 'teacher' && pointerActive && pointerPosition.x > 0 && (
              <div
                className="pointer-events-none absolute z-40 transition-all duration-75"
                style={{
                  left: pointerPosition.x,
                  top: pointerPosition.y,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {effectivePresentationShared && (
                  <div className="absolute -inset-3 bg-brand-primary/20 rounded-full animate-ping" />
                )}
                <div className="relative w-6 h-6 bg-brand-primary rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              </div>
            )}

            {/* Teacher Pointer - mirrored for students from synced state (percent coords) */}
            {userRole === 'student' && effectivePresentationShared
              && syncedPointerState?.active
              && typeof syncedPointerState.x === 'number'
              && typeof syncedPointerState.y === 'number' && (
              <div
                className="pointer-events-none absolute z-40 transition-all duration-75"
                style={{
                  left: `${syncedPointerState.x}%`,
                  top: `${syncedPointerState.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <div className="absolute -inset-3 bg-brand-primary/20 rounded-full animate-ping" />
                <div className="relative w-6 h-6 bg-brand-primary rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              </div>
            )}

          </div>
        </div>

      {/* Floating Toolbar.
          Mobile: absolute-positioned vertical rail on the right edge of the presentation area
          (below the video PiP), with pill on top and End Lesson at the bottom.
          Desktop: normal horizontal bar centered at the bottom.
          Suppressed while the rating modal is up so it doesn't peek through the backdrop. */}
      {!showRatingModal && (
      <div className={`absolute right-2 top-20 bottom-2 z-20 flex flex-col items-center justify-between gap-2 md:relative md:z-auto md:right-auto md:top-auto md:bottom-auto md:transform-none md:translate-x-0 md:flex-row md:justify-center md:py-4 md:gap-3 ${chatOpen ? 'relative z-auto right-auto top-auto bottom-auto flex-row justify-center py-2 gap-3' : ''}`}>
        {/* Pencil Color & Thickness Picker — mobile: floats to the left of the rail; desktop: above the bar. */}
        {drawingTool === 'pencil' && (userRole === 'teacher' || (userRole === 'student' && effectivePresentationShared)) && (
          <div className={`absolute right-full top-1/2 -translate-y-1/2 mr-2 md:right-auto md:left-1/2 md:top-auto md:mr-0 md:bottom-full md:mb-2 md:-translate-x-1/2 md:translate-y-0 z-30 flex flex-col items-center gap-2 rounded-2xl px-2 py-3 shadow-xl md:flex-row md:gap-3 md:px-3 md:py-2 ${chatOpen ? 'right-auto left-1/2 top-auto mr-0 bottom-full mb-2 -translate-x-1/2 translate-y-0 flex-row gap-3 px-3 py-2' : ''}`} style={{ backgroundColor: '#2A4D25' }}>
            {/* Colors */}
            <div className={`flex flex-col items-center gap-1.5 md:flex-row ${chatOpen ? 'flex-row' : ''}`}>
              {['#000000', '#FFFFFF', '#E11D48', '#F59E0B', '#10B981', '#2563EB', '#7C3AED'].map((c) => (
                <button
                  key={c}
                  onClick={() => setDrawingColor(c)}
                  className={`w-6 h-6 rounded-full transition ${drawingColor === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#2A4D25]' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c, border: c === '#FFFFFF' ? '1px solid #ccc' : 'none' }}
                  title={tt('colorTooltip', { color: c })}
                />
              ))}
            </div>
            <div className={`h-px w-6 bg-white/20 md:h-6 md:w-px ${chatOpen ? '!h-6 !w-px' : ''}`} />
            {/* Thickness */}
            <div className={`flex flex-col items-center gap-1.5 md:flex-row ${chatOpen ? 'flex-row' : ''}`}>
              {[2, 4, 7, 11].map((sz) => (
                <button
                  key={sz}
                  onClick={() => setDrawingSize(sz)}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${drawingSize === sz ? 'bg-white' : 'hover:bg-white/10'}`}
                  title={tt('thicknessTooltip', { size: sz })}
                >
                  <span
                    className="rounded-full"
                    style={{
                      width: `${sz + 2}px`,
                      height: `${sz + 2}px`,
                      backgroundColor: drawingSize === sz ? (drawingColor === '#FFFFFF' ? '#2A4D25' : drawingColor) : '#FFFFFF'
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={`flex flex-col items-center gap-1.5 rounded-2xl px-1.5 py-2 shadow-xl scrollbar-hide max-h-full overflow-y-auto md:flex-row md:gap-3 md:px-4 md:py-3 md:overflow-visible md:max-h-none md:max-w-full ${chatOpen ? 'flex-row gap-2 px-3 py-2 overflow-visible max-h-none max-w-full' : ''}`} style={{ backgroundColor: '#2A4D25' }}>
        {/* Left - Media Controls */}
        {videoToken && livekitUrl ? (
          <VideoControls showScreenShare={false} className={chatOpen ? '!flex-row' : ''} />
        ) : (
          <div className={`flex flex-col md:flex-row items-center gap-2 ${chatOpen ? 'flex-row' : ''}`}>
            <button
              disabled
              className="text-white/40 p-2 rounded-lg cursor-not-allowed"
              title={tt('micUnavailable')}
            >
              <Mic size={20} />
            </button>
            <button
              disabled
              className="text-white/40 p-2 rounded-lg cursor-not-allowed"
              title={tt('cameraUnavailable')}
            >
              <Camera size={20} />
            </button>
          </div>
        )}

        <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />

        {/* Student - Magnifier and Drawing Tools */}
        {userRole === 'student' && effectivePresentationShared && (
          <>
            <button
              onClick={() => { setMagnifierActive(!magnifierActive); setDrawingTool(null); }}
              className={`p-2 rounded-lg transition ${magnifierActive ? 'bg-white text-[#2A4D25]' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              title={tt('zoom')}
            >
              <Search size={20} />
            </button>
            {/* Student Pencil Tool */}
            <button
              onClick={() => toggleDrawingTool('pencil')}
              className={`p-2 rounded-lg transition ${drawingTool === 'pencil' ? 'bg-white text-[#2A4D25]' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              title={tt('pencil')}
            >
              <Pencil size={20} />
            </button>
            {/* Student Eraser Tool */}
            <button
              onClick={() => toggleDrawingTool('eraser')}
              className={`p-2 rounded-lg transition ${drawingTool === 'eraser' ? 'bg-white text-[#2A4D25]' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              title={tt('eraser')}
            >
              <Eraser size={20} />
            </button>
            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />
          </>
        )}

        {/* Center - Teacher Controls */}
        {userRole === 'teacher' && (
          <>
            {/* Share Presentation Button */}
            <button
              onClick={handleTogglePresentationShare}
              disabled={!presentationShared && !teacherPresentation}
              className={`px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                presentationShared
                  ? 'bg-brand-red hover:bg-brand-red/90 text-white'
                  : 'bg-white hover:bg-brand-cream text-brand-secondary'
              }`}
              title={
                presentationShared
                  ? tt('stopSharing')
                  : teacherPresentation
                    ? tt('sharePresentation')
                    : tt('noPresentationAttached')
              }
            >
              <Share2 size={14} />
              {presentationShared ? tt('stop') : tt('show')}
            </button>

            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />

            {/* Drawing Tools */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setPointerActive(!pointerActive); setLaserActive(false); setHighlighterActive(false); setDrawingTool(null); }}
                className={`p-2 rounded-lg transition ${pointerActive ? 'bg-white text-brand-secondary' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
                title={tt('pointer')}
              >
                <MousePointer size={18} />
              </button>
              {/* Pencil Tool */}
              <button
                onClick={() => toggleDrawingTool('pencil')}
                className={`p-2 rounded-lg transition ${drawingTool === 'pencil' ? 'bg-white text-brand-secondary' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
                title={tt('pencil')}
              >
                <Pencil size={18} />
              </button>
              {/* Eraser Tool */}
              <button
                onClick={() => toggleDrawingTool('eraser')}
                className={`p-2 rounded-lg transition ${drawingTool === 'eraser' ? 'bg-white text-brand-secondary' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
                title={tt('eraser')}
              >
                <Eraser size={18} />
              </button>
              {/* Clear All Drawings */}
              {drawingStrokes.length > 0 && (
                <button
                  onClick={handleClearAllDrawings}
                  className="p-2 rounded-lg transition text-white/80 hover:text-brand-red hover:bg-white/10"
                  title={tt('clearAllDrawings')}
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>

            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />

            {/* Timer Button */}
            <button
              onClick={() => setShowTimerModal(true)}
              className={`p-2 rounded-lg transition ${timerActive ? 'bg-white text-brand-secondary' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              title={tt('timer')}
            >
              <Timer size={18} />
            </button>

            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />

            {/* Slide Navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevSlide}
                disabled={classSlideIndex === 0}
                className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-white text-sm min-w-[50px] text-center font-medium">
                {effectiveSlideIndex + 1} / {teacherPresentation?.slides.length || 1}
              </span>
              <button
                onClick={handleNextSlide}
                disabled={!teacherPresentation || classSlideIndex >= teacherPresentation.slides.length - 1}
                className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Check-all button — always rendered so the toolbar layout doesn't shift between slides.
                Disabled when the current slide has no checkable exercises. */}
            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />
            <button
              onClick={handleCheckAllOnSlide}
              disabled={!slideHasCheckable}
              className={`px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-2 text-sm ${
                slideHasCheckable
                  ? 'bg-white hover:bg-brand-cream text-brand-secondary'
                  : 'bg-white/30 text-white/60 cursor-not-allowed'
              }`}
              title={slideHasCheckable ? tt('checkAllExercises') : tt('noExercisesToCheck')}
            >
              <Check size={14} />
              {tt('check')}
            </button>

            <div className={`h-px w-6 md:h-8 md:w-px bg-white/20 ${chatOpen ? '!h-8 !w-px' : ''}`} />
          </>
        )}

          {/* Homework is auto-assigned by the backend 60 min after scheduled start. No manual UI. */}

          {/* Mobile-only: Chat toggle */}
          <button
            onClick={() => setChatOpen(true)}
            className="lg:hidden p-2 rounded-lg transition text-white/80 hover:text-white hover:bg-white/10"
            title={tt('chat')}
          >
            <MessageCircle size={20} />
          </button>
        </div>

        {/* End Lesson Button — kept outside the scrollable pill so it's always visible
            (otherwise on narrow landscape phones the pill overflows and the button is cut off).
            On mobile we collapse the label to just the X icon to save horizontal space. */}
        <button
          onClick={handleEndLesson}
          className={`bg-brand-red hover:bg-brand-red/90 text-white p-2 md:px-4 md:py-2 rounded-lg transition font-semibold text-sm shadow-xl flex-shrink-0 flex items-center justify-center gap-2 ${chatOpen ? 'px-4 py-2' : ''}`}
          aria-label={tt('endLessonAria')}
        >
          <X size={18} className={`md:hidden ${chatOpen ? '!hidden' : ''}`} />
          <span className={`hidden md:inline ${chatOpen ? '!inline' : ''}`}>{tt('endLesson')}</span>
        </button>
      </div>
      )}
      </div>

        {/* Right Panel - Chat. Always an inline flex sibling (no fixed drawer overlay) so it sits
            side-by-side with the presentation. On lg+ it's always visible; on landscape phones and
            small tablets it's hidden by default and toggled open with the toolbar chat button. */}
        <div
          className={`bg-brand-cream shadow-sm flex-col overflow-hidden rounded-2xl
            w-[40%] max-w-[420px] m-4 ml-0
            lg:w-[30%] lg:min-w-[360px] lg:max-w-none
            ${chatOpen ? 'flex' : 'hidden lg:flex'}`}
        >
          {/* Chat Header — teachers get Chat/Notes tabs; everyone else just sees the Chat title. */}
          <div className="border-b border-brand-light/50 bg-brand-cream/30 rounded-t-2xl relative">
            {userRole === 'teacher' ? (
              <div className="flex items-stretch">
                {/* Live Vocabulary: push a word into a student's homework mid-lesson.
                    Hidden on club lessons since they don't have per-student homework. */}
                {!isClubLesson && (
                  <button
                    onClick={() => setShowLiveVocabModal(true)}
                    className="flex-shrink-0 px-3 flex items-center justify-center text-brand-primary/60 hover:text-brand-primary hover:bg-brand-light/30 border-b-2 border-transparent transition"
                    title={tt('liveVocab.buttonTitle')}
                  >
                    <Languages size={16} />
                  </button>
                )}
                <button
                  onClick={() => setSidebarTab('chat')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                    sidebarTab === 'chat'
                      ? 'text-brand-primary border-b-2 border-brand-primary'
                      : 'text-brand-primary/60 hover:text-brand-primary border-b-2 border-transparent'
                  }`}
                >
                  <MessageCircle size={16} />
                  {tt('chat')}
                </button>
                <button
                  onClick={() => setSidebarTab('notes')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                    sidebarTab === 'notes'
                      ? 'text-brand-primary border-b-2 border-brand-primary'
                      : 'text-brand-primary/60 hover:text-brand-primary border-b-2 border-transparent'
                  }`}
                  title={tt('slideNotesTooltip')}
                >
                  <StickyNote size={16} />
                  {tt('notes')}
                </button>
              </div>
            ) : (
              <div className="py-3 px-4 flex items-center justify-center gap-2 text-brand-primary font-medium text-sm">
                <MessageCircle size={16} />
                {tt('chat')}
              </div>
            )}
            {/* Close button — visible on <lg since desktop keeps chat permanently open */}
            <button
              onClick={() => setChatOpen(false)}
              className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-brand-primary/70 hover:text-brand-primary hover:bg-brand-light/40"
              title={tt('closeChat')}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — mount ClassroomChat always so its state (input, scroll) survives tab switches;
              hide it when the teacher is on the Notes tab. */}
          <div className={`flex-1 min-h-0 flex flex-col ${userRole === 'teacher' && sidebarTab === 'notes' ? 'hidden' : ''}`}>
            <ClassroomChat
              chatAvailable={chatAvailable}
              isClubLesson={isClubLesson}
              clubMessages={clubMessages}
              messages={messages}
              userId={user?.id}
              onSendMessage={handleSendIndividualMessage}
              onSendClubMessage={handleSendClubChatMessage}
            />
          </div>
          {userRole === 'teacher' && sidebarTab === 'notes' && (
            <div className="flex-1 min-h-0 overflow-y-auto p-4 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
              {classSlide?.teacherNotes?.trim()
                ? renderTeacherNotes(classSlide.teacherNotes)
                : <span className="text-brand-primary/50 italic">—</span>}
            </div>
          )}

        </div>

      {/* Timer Modal - Teacher Only */}
      {userRole === 'teacher' && (
        <TimerModal
          isOpen={showTimerModal}
          onClose={() => setShowTimerModal(false)}
          timerActive={timerActive}
          timerRemainingSeconds={timerRemainingSeconds}
          onStartTimer={handleStartTimer}
          onStopTimer={handleStopTimer}
          formatTime={formatTime}
        />
      )}

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onSubmit={handleSubmitRating}
        onSkip={handleSkipRating}
      />

      {/* Teacher Report Modal */}
      <TeacherReportModal
        isOpen={showTeacherReport}
        onClose={() => setShowTeacherReport(false)}
        onSubmit={handleSubmitTeacherReport}
      />

      {/* Live Vocabulary Modal - teacher pushes a word into a student's homework */}
      {userRole === 'teacher' && showLiveVocabModal && currentLessonForClass?.id && (() => {
        // Build the picker list: presence-tracked students first, then the
        // primary booked student as a fallback if presence hasn't loaded yet.
        const primaryId = currentLessonForClass?.studentId || currentLessonForClass?.student_id;
        const primaryName = currentLessonForClass?.studentName || currentLessonForClass?.student_name || tt('liveVocab.student');
        const studentPickerList = (() => {
          const online = (participants || [])
            .filter((p) => p.role === 'student')
            .map((p) => ({ id: p.id, name: p.name || primaryName }));
          if (online.length > 0) return online;
          return primaryId ? [{ id: primaryId, name: primaryName }] : [];
        })();
        if (studentPickerList.length === 0) return null;
        return (
          <LiveVocabularyModal
            lessonScheduleId={currentLessonForClass.id}
            students={studentPickerList}
            defaultStudentId={primaryId}
            onClose={() => setShowLiveVocabModal(false)}
          />
        );
      })()}

    </div>

    {/* Alert Modal */}
    <AlertModal
      isOpen={alertModal.isOpen}
      onClose={closeAlert}
      title={alertModal.title}
      message={alertModal.message}
      type={alertModal.type}
    />

    {/* Auto-end warning: floating card in top-center, appears at scheduled_end + 5min.
        Non-blocking so the teacher can still submit their report / student can finish
        answering before the +10min hard cutoff. */}
    {autoEndWarningActive && (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[110] pointer-events-none">
        <div className="bg-white/95 backdrop-blur border border-amber-300 rounded-xl shadow-xl px-4 py-3 flex items-center gap-3 pointer-events-auto">
          <AlertTriangle size={20} className="text-amber-500 shrink-0" />
          <div className="text-sm text-gray-800 leading-tight">
            <div className="font-semibold">Час уроку сплив</div>
            <div className="text-gray-600">Автоматичне завершення через</div>
          </div>
          <div className="text-2xl font-bold text-amber-600 tabular-nums pl-2 border-l border-amber-200">
            {Math.floor(autoEndRemainingSec / 60)}:{String(autoEndRemainingSec % 60).padStart(2, '0')}
          </div>
        </div>
      </div>
    )}

    {/* Brief overlay while handleEndLesson fetches the student's stars. */}
    {endingLesson && userRole === 'student' && (
      <div className="fixed inset-0 bg-black/40 z-[95] flex items-center justify-center p-4">
        <div className="rounded-2xl bg-white p-6 shadow-xl text-center max-w-xs">
          <div className="mx-auto mb-3 w-8 h-8 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: '#2A4D25' }} />
          <p className="text-gray-800 font-medium">{tt('countingStars')}</p>
        </div>
      </div>
    )}

    {/* Combined lesson-end modal — shows earned stars (from exercises) AND
        the lesson-quality rating in a single view so students can submit
        feedback in one step. */}
    {showStarsResult && starsResult && userRole === 'student' && (
      <StarsResultModal
        stars={starsResult.stars}
        percentage={starsResult.percentage}
        earnedPoints={starsResult.earnedPoints}
        totalPoints={starsResult.totalPoints}
        onSubmit={(ratingData) => {
          setShowStarsResult(false);
          setStarsResult(null);
          handleSubmitRating(ratingData);
        }}
        onSkip={() => {
          setShowStarsResult(false);
          setStarsResult(null);
          handleSkipRating();
        }}
      />
    )}
    </>
    );

    // Wrap in VideoRoom if we have a token, otherwise render without video.
    // TeacherNotesBroadcaster must live inside VideoRoom — it uses LiveKit
    // hooks that throw without a <LiveKitRoom> provider, which would blank the
    // whole lesson for teachers before the video token is ready.
    if (videoToken && livekitUrl) {
      return (
        <VideoRoom
          token={videoToken}
          serverUrl={livekitUrl}
          initialCameraEnabled={initialCameraEnabled}
          initialMicEnabled={initialMicEnabled}
        >
          {userRole === 'teacher' && (
            <TeacherNotesBroadcaster
              open={sidebarTab === 'notes'}
              text={classSlide?.teacherNotes || ''}
            />
          )}
          {userRole === 'teacher' && (
            <SlideCapturePublisher boardRef={boardRef} />
          )}
          {/* Records the lesson in the teacher's browser when the backend says so
              (admin-requested individual lesson, or any group lesson). */}
          {userRole === 'teacher' && !isClubLesson && (
            <LessonRecorder lessonId={currentLessonForClass?.id} />
          )}
          {classroomContent}
        </VideoRoom>
      );
    }

    return classroomContent;
};

export default VirtualClassroom;
